// The town, assembled from the kit: every building wall sits exactly on its sim collision
// box (world.boxes kind 'building'); roofs, awnings, balconies, oriels, signs and wires are
// the only things that overhang. Layout follows western_town.png: narrow two-and-three storey
// clapboard fronts in teal / sea green / weathered wood with cream trim, gables and gambrels
// and false fronts, striped awnings, a raised plank boardwalk with a stone curb, telephone
// poles with a mess of sagging wires, and the street clutter that makes it lived in.

import * as THREE from 'three';
import type { World, Box, Cyl } from '../../../sim/world';
import { hash32, fbm as nfbm } from '../../../sim/core';
import { Jit } from './geo';
import { KitGeo, RGB } from './town_kit';
import type { TileName } from './town_atlas';
import type { Canopy } from './town_canopy';
import { layoutRuin, RuinLayout } from './town_ruin';
import { Frame, WORLD, P3, wall, wallHoles, reveal, Hole, decal, box, beam, cyl, disc, crossCards, hangStrip, catenary, AoFn } from './town_frame';
import { Clock, type Pause } from '../warm';

/** openings registered per wall frame while a building's facade is laid out */
const holeMap = new Map<Frame, Hole[]>();
let lastRoof = '';
let roofQueue: RoofKind[] = [];
let roofSide = 0;
function hole(F: Frame, h: Hole) { holeMap.get(F)?.push(h); }

const _c = new THREE.Color();
/** sRGB hex to a linear tint (times k: the painted atlas is ~0.85 grey). */
function lin(hex: number, k = 1): RGB {
  _c.setHex(hex);
  return [_c.r * k, _c.g * k, _c.b * k];
}
function mulv(c: RGB, k: RGB): RGB { return [c[0] * k[0], c[1] * k[1], c[2] * k[2]]; }
function mul(c: RGB, k: number): RGB { return [c[0] * k, c[1] * k, c[2] * k]; }
function mix(a: RGB, b: RGB, t: number): RGB { return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]; }
const ss = (a: number, b: number, x: number) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

// tints lean cyan: the warm sun, gold sky fill and print grade pull them back to the ref's teal
// calibrated on screen against the ref (teal ~#4f9a9a lit, #5d887c in shade): the warm sun,
// gold sky fill and print grade push everything yellow-green, so the tints lean blue and grey
const WB: RGB = [1.2, 1.0, 1.22];
const WALLS: RGB[] = [
  lin(0x3f7f88, 1.35), lin(0x4a8a90, 1.3), lin(0x55949a, 1.25), lin(0x37737e, 1.4), lin(0x5e9c9c, 1.2),
  lin(0x4a8a84, 1.3), lin(0x5a9488, 1.2), lin(0x8d6c4e, 1.25), lin(0xd6c4a0, 1.05), lin(0x4c7f8e, 1.3),
].map((c, i) => (i === 7 || i === 8 ? c : mulv(c, WB)));
// teal-heavy pick table (the ref is mostly teal and sea green, a few weathered-wood and cream fronts)
const WALL_PICK = [0, 1, 2, 3, 4, 0, 1, 5, 2, 9, 6, 7, 7, 8, 3, 1];
const TB: RGB = [0.84, 0.86, 1.22];
const TRIMS: RGB[] = [mulv(lin(0xf2e8cc, 1.12), TB), mulv(lin(0xefe2c0, 1.12), TB), mulv(lin(0xe8dcc0, 1.1), TB), lin(0x6a4e36, 1.2)];
const ACCENTS: RGB[] = [lin(0x2c5e5a, 1.3), lin(0x9a4a34, 1.2), lin(0xc9a24a, 1.1), lin(0x5d7a46, 1.2), lin(0x3d5f7a, 1.2), lin(0x7d5236, 1.2)];
const SHINGLE: RGB[] = [lin(0x8a6a52, 1.2), lin(0x7a5240, 1.25), lin(0x5e6a66, 1.3), lin(0x8e6a4c, 1.15), lin(0x6e5a4a, 1.3)];
const RUSTY: RGB[] = [lin(0xe8b48a, 1.05), lin(0xd09468, 1.05), lin(0xc8bcae, 1.0), lin(0xe0a070, 1.05), lin(0xb48a6a, 1.1)];
const RIDGE: RGB = lin(0x8a5a3e, 1.1);
const WOOD: RGB = lin(0xffffff, 1.0);
const WOOD_DARK: RGB = lin(0xb8a890, 0.9);
const IRON: RGB = lin(0x3a3530, 1);
const W1: RGB = [1, 1, 1];

const WIN: TileName[] = ['win0', 'win1', 'win2', 'win3', 'win4'];
const SHOP: TileName[] = ['shop0', 'shop1', 'shop2'];
const DOORS: TileName[] = ['doorGreen', 'doorWood', 'doorRed', 'doorFridge', 'doorBatwing'];
const SIGNS: TileName[] = ['sign0', 'sign1', 'sign2', 'sign3', 'sign4', 'sign5', 'sign6', 'sign7', 'sign8', 'sign9'];
const RSIGN: TileName[] = ['rsignHotdog', 'rsignCafe', 'rsignBolt'];
const AWN: TileName[] = ['awnRed', 'awnTeal', 'awnYellow', 'awnBlue', 'awnRed'];
const BRIGHT: RGB[] = [lin(0xc8372d, 1.25), lin(0xe8a82a, 1.15), lin(0x2f6fb0, 1.3), lin(0x3f8f4a, 1.25), lin(0xd84a7a, 1.2)];
const POSTERS: TileName[] = ['poster0', 'poster1', 'poster2', 'poster3'];

export interface TownParts {
  /** opaque atlas geometry (walls, roofs, trim, decals) */
  solid: KitGeo;
  /** alpha-cut atlas geometry (awnings, foliage, posters, bunting, railings) */
  cut: KitGeo;
  /** wire catenaries, xyz pairs */
  wires: number[];
  /** leaf clusters for the tree canopies (town_canopy.ts) */
  canopies: Canopy[];
  /** per-building runs, tags and hidden rubble, for burning the town down (town_ruin.ts) */
  ruin: RuinLayout;
}

interface Ctx {
  world: World;
  g: KitGeo;
  cut: KitGeo;
  wires: number[];
  ty: number;
  q: number;
  /** service-wire attach points per side */
  attach: P3[];
  /** facade tops for street-crossing wires and bunting, per side */
  tops: { x: number; y: number; z: number; side: number }[];
  signIx: number;
  /** boardwalk decks, so props set on them can lay a contact shadow in the planks */
  decks: Deck[];
  canopies: Canopy[];
}
interface Deck { xa: number; xb: number; z0: number; z1: number; y: number; side: number }

function deckOf(world: World, row: Box[], side: number): Deck | null {
  if (!row.length) return null;
  const t = world.town;
  const xa = Math.min(...row.map((b) => b.x - b.hx)) - 1.2, xb = Math.max(...row.map((b) => b.x + b.hx)) + 1.2;
  const zs = t.z + side * t.halfWidth, zw = t.z + side * (t.halfWidth + 2.2);
  return { xa, xb, z0: Math.min(zs, zw), z1: Math.max(zs, zw), y: t.y + 0.15, side };
}
/** the deck's own AO across its width (matches the deck quad in boardwalk()) */
/** A child stream drawn from rng with exactly one draw, so a quality-gated branch can use as
 *  many numbers as it likes without shifting every later choice (layout must not depend on q). */
function fork(r: Jit) { return new Jit(Math.floor(r.next() * 2147483646)); }
function deckAo(d: Deck, z: number) {
  const f = (z - d.z0) / (d.z1 - d.z0); // 0 at z0, 1 at z1
  return d.side > 0 ? 1 + (0.86 - 1) * f : 0.86 + (1 - 0.86) * f;
}
const DECK_TINT: RGB = lin(0xfff0d6, 1.22);
/** A soft contact shadow under a prop on a boardwalk: a planks fan, dark in the middle. */
function blob(ctx: Ctx, x: number, z: number, r: number, y0: number) {
  if (ctx.q < 1) return;
  const d = ctx.decks.find((k) => x > k.xa && x < k.xb && z > k.z0 - 0.05 && z < k.z1 + 0.05);
  if (!d || Math.abs(y0 - d.y) > 0.05) return;
  const y = d.y + 0.006, M = 2, N = 10, R = r * 1.45;
  const c: P3 = [x, y, z];
  for (let i = 0; i < N; i++) {
    const a0 = (i / N) * Math.PI * 2, a1 = ((i + 1) / N) * Math.PI * 2;
    const p0: P3 = [x + Math.cos(a0) * R, y, Math.min(d.z1, Math.max(d.z0, z + Math.sin(a0) * R))];
    const p1: P3 = [x + Math.cos(a1) * R, y, Math.min(d.z1, Math.max(d.z0, z + Math.sin(a1) * R))];
    // up-facing winding
    ctx.g.tri([c, p1, p0], [[c[0] / M, c[2] / M], [p1[0] / M, p1[2] / M], [p0[0] / M, p0[2] / M]], 'planks', DECK_TINT, [deckAo(d, z) * 0.5, deckAo(d, p1[2]), deckAo(d, p0[2])]);
  }
}

/** (in slices: a background island build, client/render/warm.ts) */
export function* buildTownPartsSteps(world: World, quality: number, pause: Pause): Generator<unknown, TownParts, unknown> {
  const clk = new Clock(pause);
  const t = world.town;
  const ctx: Ctx = { world, g: new KitGeo(), cut: new KitGeo(), wires: [], ty: t.y, q: quality, attach: [], tops: [], signIx: world.seed % 7, decks: [], canopies: [] };
  const buildings = world.boxes.filter((b) => b.kind === 'building');
  lastRoof = ''; roofQueue = []; roofSide = 0;
  for (const side of [-1, 1]) {
    const d = deckOf(world, buildings.filter((b) => Math.sign(b.z - t.z) === side), side);
    if (d) ctx.decks.push(d);
  }
  const ranges: { s0: number; s1: number; c0: number; c1: number }[] = [];
  for (const b of buildings) {
    yield* clk.tick('town:building');
    const s0 = ctx.g.triCount, c0 = ctx.cut.triCount;
    building(ctx, b);
    ranges.push({ s0, s1: ctx.g.triCount, c0, c1: ctx.cut.triCount });
  }
  yield* clk.tick('town:street');
  street(ctx);
  for (const side of [-1, 1]) { yield* clk.tick('town:boardwalk'); boardwalk(ctx, buildings.filter((b) => Math.sign(b.z - t.z) === side), side); }
  yield* clk.tick('town:alleys');
  alleys(ctx, buildings);
  yield* clk.tick('town:dressing');
  nearDressing(ctx);
  const poles = world.cyls.filter((c) => c.kind === 'pole').sort((a, b) => a.x - b.x);
  yield* clk.tick('town:wires');
  wiresAndPoles(ctx, poles);
  for (const c of world.cyls) if (c.kind === 'tank') { yield* clk.tick('town:tank'); tank(ctx, c, poles); }
  yield* clk.tick('town:vista');
  vista(ctx, buildings, poles);
  yield* clk.tick('town:ruin');
  const ruin = layoutRuin(world, buildings, ctx.g, ctx.cut, ranges);
  return { solid: ctx.g, cut: ctx.cut, wires: ctx.wires, canopies: ctx.canopies, ruin };
}

// ------------------------------------------------------------------ buildings
type RoofKind = 'gable' | 'gambrel' | 'false' | 'side' | 'hip';

interface Bay {
  u0: number; u1: number;
  wallTile: TileName; wall: RGB; trim: RGB; accent: RGB;
  roof: RoofKind; roofTile: TileName; roofCol: RGB; pitch: number;
  balcony: boolean; oriel: boolean; tv: boolean; shutters: boolean; awning: number; porch: boolean;
  outerL: boolean; outerR: boolean;
}

function building(ctx: Ctx, b: Box) {
  const { g, ty } = ctx;
  const t = ctx.world.town;
  const side = Math.sign(b.z - t.z) || 1;
  const zf = b.z - side * b.hz;
  const F = new Frame(b.x, zf, side > 0 ? -1 : 1, 0);
  const D = 2 * b.hz; // depth: facade at w=0, back wall at w=-D
  const hx = b.hx;
  const y1 = b.y1;
  const yb = ty - 0.6;
  const H = y1 - ty;
  const rng = new Jit(hash32(ctx.world.seed, Math.round(b.x * 10), Math.round(b.z * 10)));
  const floors = H > 7.5 ? 3 : 2;
  const s0 = g.pos.length, c0 = ctx.cut.pos.length;
  const g0 = 3.5; // ground floor height
  const fh = (H - g0) / (floors - 1);

  // bays: wide lots are two narrow fronts (the ref's tall narrow houses)
  const bays: Bay[] = [];
  const cuts = [-hx];
  if (2 * hx > 8.4) cuts.push(-hx + 2 * hx * rng.range(0.42, 0.58));
  cuts.push(hx);
  let lastWall = -1;
  for (let i = 0; i + 1 < cuts.length; i++) {
    let wi = WALL_PICK[(rng.next() * WALL_PICK.length) | 0];
    if (wi === lastWall) wi = WALL_PICK[(wi * 7 + 3) % WALL_PICK.length];
    const force = (globalThis as { __townWall?: number }).__townWall; // lab calibration hook
    if (force !== undefined) wi = force;
    lastWall = wi;
    const bw = cuts[i + 1] - cuts[i];
    // roofs come in groups of three down each row: a flat false-front, a hip and one of
    // gable / gambrel / side-gable, shuffled, never the same kind twice running
    if (roofSide !== side) { roofSide = side; roofQueue = []; lastRoof = ''; }
    if (!roofQueue.length) {
      const p1: RoofKind = rng.next() < 0.5 ? 'gable' : 'gambrel';
      const p2: RoofKind = (['hip', 'side', 'gable'] as RoofKind[])[(rng.next() * 3) | 0];
      const grp: RoofKind[] = ['false', p1, p2 === p1 ? 'hip' : p2];
      for (let k = 2; k > 0; k--) { const j = (rng.next() * (k + 1)) | 0; [grp[k], grp[j]] = [grp[j], grp[k]]; }
      if (grp[0] === lastRoof) grp.push(grp.shift()!);
      roofQueue = grp;
    }
    let roof = roofQueue.shift()!;
    if (roof === 'gambrel' && H < 6) roof = 'gable';
    lastRoof = roof;
    const corr = rng.next() < 0.72;
    bays.push({
      u0: cuts[i], u1: cuts[i + 1],
      wallTile: rng.next() < 0.5 ? 'clap' : 'clap2',
      // per-building fade: value and a little hue drift so no two fronts are the same paint
      wall: mulv(WALLS[wi], [rng.range(0.86, 1.08), rng.range(0.9, 1.06), rng.range(0.88, 1.08)]),
      trim: wi === 8 ? TRIMS[3] : TRIMS[(rng.next() * 3) | 0],
      accent: ACCENTS[(rng.next() * ACCENTS.length) | 0],
      roof, roofTile: corr ? 'corrugated' : 'shingle',
      roofCol: corr ? RUSTY[(rng.next() * RUSTY.length) | 0] : SHINGLE[(rng.next() * SHINGLE.length) | 0],
      pitch: rng.range(0.7, 0.95),
      balcony: floors === 3 && bw > 3.4 && rng.next() < 0.42,
      oriel: bw > 3.4 && rng.next() < 0.4,
      tv: rng.next() < 0.22,
      shutters: rng.next() < 0.45,
      awning: rng.next() < 0.86 ? (rng.next() * AWN.length) | 0 : -1,
      porch: false,
      outerL: i === 0, outerR: i + 1 === cuts.length - 1,
    });
  }
  for (const by of bays) if (by.balcony) { by.awning = -1; by.porch = true; }
  for (const by of bays) if (!by.balcony && by.awning < 0 && rng.next() < 0.5) by.porch = true;

  const bands: number[] = [];
  for (let f = 1; f < floors; f++) bands.push(ty + g0 + (f - 1) * fh - 0.12);
  // inked shadow bands: the wall darkens hard just under the eave and under each floor band
  // (where the awning, balcony and band shade it), then recovers within ~0.7 m
  const ao: AoFn = (y) => {
    let k = (0.5 + 0.5 * ss(ty - 0.3, ty + 1.6, y)) * (1 - 0.45 * ss(y1 - 0.9, y1 - 0.05, y));
    for (const yb2 of bands) if (y < yb2 + 0.01) k *= 1 - 0.55 * ss(yb2 - 1.1, yb2, y);
    // the ground floor lives in porch and awning shade
    if (bands.length && y < bands[0] + 0.01) k *= 0.7;
    return k;
  };
  const wallCuts = [ty, ty + 0.9, ty + 1.8, y1 - 0.9, y1 - 0.05];
  for (const yb2 of bands) wallCuts.push(yb2 - 1.1, yb2 - 0.4, yb2);
  // alley walls sit in deep shade between the buildings
  const aoSide: AoFn = (y) => ao(y) * 0.66;
  wallCuts.sort((p, q) => p - q);

  // ---- wall frames (walls are drawn after the facade is laid out, with its openings cut)
  const sideR = new Frame(...frameArgs(F, hx, 0, -Math.PI / 2));
  const sideL = new Frame(...frameArgs(F, -hx, -D, Math.PI / 2));
  const back = new Frame(...frameArgs(F, 0, -D, Math.PI));
  const bR = bays[bays.length - 1], bL = bays[0];
  holeMap.clear();
  holeMap.set(F, []); holeMap.set(sideR, []); holeMap.set(sideL, []);
  // stone foundation (proud 4 cm) on the sides and back, visible down the alleys
  box(g, sideR, -0.04, D + 0.04, yb, ty + 0.35, -0.04, 0.05, 'stone', lin(0xd8cdb4, 1.05), { skip: 'd' });
  box(g, sideL, -0.04, D + 0.04, yb, ty + 0.35, -0.04, 0.05, 'stone', lin(0xd8cdb4, 1.05), { skip: 'd' });
  box(g, back, -hx - 0.04, hx + 0.04, yb, ty + 0.35, -0.04, 0.05, 'stone', lin(0xd8cdb4, 1.05), { skip: 'd' });

  // ---- trim: corner boards, bay seams, floor bands, eave cornice
  const tr = bL.trim;
  for (const [u, w] of [[-hx, 0], [hx, 0], [-hx, -D], [hx, -D]] as const) {
    const ua = u > 0 ? hx - 0.14 : -hx - 0.05, ub = u > 0 ? hx + 0.05 : -hx + 0.14;
    const wa = w < 0 ? -D - 0.05 : -0.14, wb = w < 0 ? -D + 0.14 : 0.05;
    box(g, F, ua, ub, ty - 0.1, y1, wa, wb, 'trim', tr, { ao, skip: 'd' });
  }
  for (let i = 1; i < bays.length; i++) box(g, F, bays[i].u0 - 0.1, bays[i].u0 + 0.1, ty, y1, 0, 0.07, 'trim', bays[i].trim, { ao, skip: 'd' });
  for (let f = 1; f < floors; f++) {
    const yfl = ty + g0 + (f - 1) * fh;
    for (const by of bays) box(g, F, by.u0, by.u1, yfl - 0.12, yfl + 0.1, 0, 0.09, 'trim', by.trim, { skip: 'd' });
    box(g, sideR, 0, D, yfl - 0.12, yfl + 0.1, 0, 0.06, 'trim', bR.trim, { skip: 'd' });
    box(g, sideL, 0, D, yfl - 0.12, yfl + 0.1, 0, 0.06, 'trim', bL.trim, { skip: 'd' });
  }

  // ---- weathering: rust and grime runs under the floor bands and the eave
  { const rq = fork(rng); // quality-independent: the stream advances the same at every tier
  if (ctx.q >= 1) {
    const runs: number[] = [];
    for (let f = 1; f < floors; f++) runs.push(ty + g0 + (f - 1) * fh - 0.12);
    for (const yr of runs) {
      for (const by of bays) if (false) grime(ctx.cut, F, by.u0 + 0.12, by.u1 - 0.12, yr, rq.range(0.4, 0.7), 0.1);
      grime(ctx.cut, sideR, 0.1, D - 0.1, yr, rq.range(0.4, 0.7), 0.07);
      grime(ctx.cut, sideL, 0.1, D - 0.1, yr, rq.range(0.4, 0.7), 0.07);
    }
  }
  }

  // ---- facade per bay
  for (let i = 0; i < bays.length; i++) bayFront(ctx, F, bays[i], b, floors, g0, fh, rng, i);

  // ---- side details: windows on the alley walls, external stairs in wide gaps
  for (const [S, by, sgn] of [[sideR, bR, 1], [sideL, bL, -1]] as const) {
    const nz = Math.max(1, Math.floor((D - 1.5) / 2.8));
    for (let f = 1; f < floors; f++) {
      const yc = ty + g0 + (f - 1) * fh + fh * 0.5;
      for (let k = 0; k < nz; k++) {
        if (rng.next() < 0.25) continue;
        const u = 0.9 + ((D - 1.8) * (k + 0.5)) / nz;
        windowUnit(ctx, S, u, yc, by, rng, false);
      }
    }
    // a small side door + lantern on the ground floor near the back
    const ud = D - 1.6;
    hole(S, [ud - 0.5, ud + 0.5, ty, ty + 2.12]);
    reveal(g, S, ud - 0.5, ud + 0.5, ty, ty + 2.12, 0.14, 'trim', by.trim);
    decal(g, S, ud - 0.5, ud + 0.5, ty, ty + 2.12, -0.14, DOORS[(rng.next() * 3) | 0], W1, 0.8);
    box(g, S, ud - 0.62, ud + 0.62, ty + 2.12, ty + 2.3, 0, 0.1, 'trim', by.trim, { skip: 'd' });
    const gap = gapBeside(ctx, b, sgn * (side > 0 ? -1 : 1));
    if (gap > 2.3 && floors >= 2 && rng.next() < 0.75) stairs(ctx, S, D, ty, ty + g0, by, rng);
  }

  // ---- a bright painted advert and fresh bills on one alley wall, seen as you walk up
  { const rq = fork(rng); // quality-independent: the stream advances the same at every tier
  if (ctx.q >= 1) {
    const S = rq.next() < 0.5 ? sideR : sideL;
    const bc = BRIGHT[(rq.next() * BRIGHT.length) | 0];
    box(g, S, 0.35, 2.45, ty + 1.0, ty + 2.1, 0, 0.04, 'plain', bc, { skip: 'd' });
    decal(g, S, 0.47, 2.33, ty + 1.08, ty + 2.02, 0.045, SIGNS[(rq.next() * SIGNS.length) | 0]);
    decal(ctx.cut, S, 2.65, 3.15, ty + 1.2, ty + 1.9, 0.012, POSTERS[(rq.next() * POSTERS.length) | 0]);
    if (rq.next() < 0.6) decal(ctx.cut, S, 3.25, 3.7, ty + 1.12, ty + 1.75, 0.012, POSTERS[(rq.next() * POSTERS.length) | 0]);
  }
  }

  // ---- walls on the collision box: front per bay, sides, back
  for (const by of bays) wallHoles(g, F, by.u0, by.u1, yb, y1, 0, by.wallTile, by.wall, ao, holeMap.get(F)!, wallCuts);
  wallHoles(g, sideR, 0, D, yb, y1, 0, bR.wallTile, mul(bR.wall, 0.97), aoSide, holeMap.get(sideR)!, wallCuts);
  wallHoles(g, sideL, 0, D, yb, y1, 0, bL.wallTile, mul(bL.wall, 0.97), aoSide, holeMap.get(sideL)!, wallCuts);
  for (const by of bays) wall(g, back, -by.u1, -by.u0, yb, y1, 0, by.wallTile, mul(by.wall, 0.92), ao, 0, wallCuts);
  holeMap.clear();

  // ---- roofs
  for (const by of bays) roof(ctx, F, by, b, y1, D, rng);
  // the second house in each row is a stacked tower house: a turret climbs from its street corner
  {
    const rank = ctx.world.boxes.filter((o) => o.kind === 'building' && Math.sign(o.z - t.z) === side && o.x < b.x).length;
    if (rank === 1 || rank === 3 || rank === 5) {
      const by = bays[bays.length - 1];
      cupola(ctx, F, hx - 1.25, -1.3, y1 - 0.2, by, rng, 2.6);
    }
  }

  // the lean: old timber settles. Each house racks 1-3 degrees along the street and a little out
  // over the boardwalk; the footprint stays on its collision box, only the upper floors move
  const kx = (rng.next() < 0.5 ? -1 : 1) * rng.range(0.018, 0.045), kz = -side * rng.range(0.004, 0.016);
  for (const [arr, from] of [[g.pos, s0], [ctx.cut.pos, c0]] as const) {
    for (let i = from; i < arr.length; i += 3) {
      const h = Math.max(0, arr[i + 1] - ty);
      arr[i] += kx * h; arr[i + 2] += kz * h;
    }
  }

  // where the street wires land, and the facade tops for bunting
  for (const by of bays) {
    ctx.attach.push(F.p((by.u0 + by.u1) / 2 + 0.6, y1 - 0.5, 0.12));
    const tp = F.p((by.u0 + by.u1) / 2, y1 - 0.3, 0.1);
    ctx.tops.push({ x: tp[0], y: tp[1], z: tp[2], side });
  }
}

/** Rust / grime runs hanging from a trim line at y (alpha-cut strip just proud of the wall). */
function grime(cut: KitGeo, F: Frame, u0: number, u1: number, y: number, len: number, w: number) {
  if (u1 - u0 < 1e9) return; // runs read as noise next to the ref: off
  const Mu = 2.4;
  cut.quad(F.p(u0, y - len, w), F.p(u1, y - len, w), F.p(u1, y, w), F.p(u0, y, w), [u0 / Mu, 0], [u1 / Mu, 0], [u1 / Mu, 1], [u0 / Mu, 1], 'drips', W1);
}

/** Frame args for a sub-frame at local (u, w), rotated a (radians, U toward W). */
function frameArgs(F: Frame, u: number, w: number, a: number): [number, number, number, number] {
  const s = F.sub(u, w, a);
  return [s.ox, s.oz, s.ux, s.uz];
}

/** Free width of the alley beside building b on the given world-x side (+1 = +x). */
function gapBeside(ctx: Ctx, b: Box, dirX: number): number {
  let best = 99;
  for (const o of ctx.world.boxes) {
    if (o === b || o.kind !== 'building' || Math.sign(o.z) !== Math.sign(b.z)) continue;
    const d = dirX > 0 ? (o.x - o.hx) - (b.x + b.hx) : (b.x - b.hx) - (o.x + o.hx);
    if (d > -0.01 && d < best) best = d;
  }
  return best;
}

function bayFront(ctx: Ctx, F: Frame, by: Bay, b: Box, floors: number, g0: number, fh: number, rng: Jit, bayIx: number) {
  const { g, cut, ty, q } = ctx;
  const u0 = by.u0, u1 = by.u1, bw = u1 - u0, uc = (u0 + u1) / 2;
  const deck = ty + 0.15;
  const tr = by.trim;

  // ---- ground floor: pilasters, door, display windows, kickplates, sign board
  box(g, F, u0 + 0.02, u0 + 0.26, deck, ty + g0 - 0.1, 0, 0.1, 'trim', tr, { skip: 'd' });
  box(g, F, u1 - 0.26, u1 - 0.02, deck, ty + g0 - 0.1, 0, 0.1, 'trim', tr, { skip: 'd' });
  const doorW = 1.1, doorH = 2.3;
  const doorLeft = bw < 4.2 ? rng.next() < 0.5 : false;
  const du = bw < 4.2 ? (doorLeft ? u0 + 0.45 + doorW / 2 : u1 - 0.45 - doorW / 2) : uc + rng.range(-0.3, 0.3);
  const doorTile = by.roof === 'false' && bayIx === 0 && rng.next() < 0.35 ? 'doorBatwing' : DOORS[(rng.next() * (rng.next() < 0.3 ? 4 : 3)) | 0];
  // recessed door: dark reveal + door + casing
  hole(F, [du - doorW / 2, du + doorW / 2, deck, deck + doorH]);
  reveal(g, F, du - doorW / 2, du + doorW / 2, deck, deck + doorH, 0.16, 'trim', tr);
  decal(g, F, du - doorW / 2, du + doorW / 2, deck, deck + doorH, -0.16, doorTile as TileName, W1, 0.85);
  box(g, F, du - doorW / 2 - 0.16, du - doorW / 2, deck, deck + doorH + 0.1, 0, 0.1, 'trim', tr, { skip: 'd' });
  box(g, F, du + doorW / 2, du + doorW / 2 + 0.16, deck, deck + doorH + 0.1, 0, 0.1, 'trim', tr, { skip: 'd' });
  box(g, F, du - doorW / 2 - 0.24, du + doorW / 2 + 0.24, deck + doorH + 0.1, deck + doorH + 0.26, 0, 0.14, 'trim', tr, { skip: 'd' });
  // transom
  hole(F, [du - doorW / 2, du + doorW / 2, deck + doorH + 0.26, deck + doorH + 0.5]);
  reveal(g, F, du - doorW / 2, du + doorW / 2, deck + doorH + 0.26, deck + doorH + 0.5, 0.1, 'trim', tr);
  decal(g, F, du - doorW / 2, du + doorW / 2, deck + doorH + 0.26, deck + doorH + 0.5, -0.1, 'win2', W1, 1, 0, 1, 0.55, 0.95);
  box(g, F, du - doorW / 2 - 0.1, du + doorW / 2 + 0.1, deck + doorH + 0.5, deck + doorH + 0.6, 0, 0.1, 'trim', tr, { skip: 'd' });
  // display windows either side of the door
  const shopT = SHOP[(rng.next() * SHOP.length) | 0];
  for (const [a, z] of [[u0 + 0.36, du - doorW / 2 - 0.3], [du + doorW / 2 + 0.3, u1 - 0.36]] as const) {
    const w = z - a;
    if (w < 0.9) continue;
    const ww = Math.min(w, 2.9), wa = (a + z) / 2 - ww / 2, wz = wa + ww;
    // kickplate panel
    box(g, F, wa - 0.08, wz + 0.08, deck, deck + 0.62, 0, 0.07, 'plain', mul(by.accent, 0.95), { skip: 'd' });
    box(g, F, wa + 0.06, wz - 0.06, deck + 0.12, deck + 0.5, 0.07, 0.09, 'plain', mul(by.accent, 1.08), { skip: 'db' });
    // glass sits back in a reveal; the sill and frame stand proud
    hole(F, [wa, wz, deck + 0.72, deck + 2.42]);
    reveal(g, F, wa, wz, deck + 0.72, deck + 2.42, 0.24, 'trim', tr);
    // mullion + transom bar standing in front of the glass
    box(g, F, (wa + wz) / 2 - 0.045, (wa + wz) / 2 + 0.045, deck + 0.72, deck + 2.42, -0.24, -0.12, 'trim', tr);
    box(g, F, wa, wz, deck + 2.0, deck + 2.08, -0.24, -0.12, 'trim', tr);
    decal(g, F, wa, wz, deck + 0.72, deck + 2.42, -0.24, ww > 1.6 ? shopT : WIN[(rng.next() * WIN.length) | 0], W1, 0.92, 0, 1, 0, 1);
    box(g, F, wa - 0.1, wz + 0.1, deck + 0.62, deck + 0.72, 0, 0.16, 'trim', tr, { skip: 'd' });
    box(g, F, wa - 0.1, wa, deck + 0.7, deck + 2.42, -0.03, 0.08, 'trim', tr, { skip: 'd' });
    box(g, F, wz, wz + 0.1, deck + 0.7, deck + 2.42, -0.03, 0.08, 'trim', tr, { skip: 'd' });
    box(g, F, wa - 0.12, wz + 0.12, deck + 2.42, deck + 2.56, -0.03, 0.1, 'trim', tr, { skip: 'd' });
    // clutter in front of the window
    { const rq = fork(rng); // quality-independent: the stream advances the same at every tier
    if (q >= 1) streetClutter(ctx, F, (wa + wz) / 2, ww, rq, by);
    }
    { const rq = fork(rng); // quality-independent: the stream advances the same at every tier
    if (q >= 2 && ww > 1.8 && rq.next() < 0.55) streetClutter(ctx, F, (wa + wz) / 2 + (rq.next() < 0.5 ? -0.6 : 0.6), ww * 0.5, rq, by);
    }
  }
  // pots by the door, and on some fronts a hitching rail + trough at the boardwalk edge
  { const rq = fork(rng); // quality-independent: the stream advances the same at every tier
  if (q >= 1 && rq.next() < 0.6) plant(ctx, F.p(du + (doorW / 2 + 0.45) * (rq.next() < 0.5 ? -1 : 1), 0, 0.3), deck, rq);
  }
  { const rq = fork(rng); // quality-independent: the stream advances the same at every tier
  if (q >= 1 && rq.next() < 0.3) {
    const hu = rq.next() < 0.5 ? u0 + 1.2 : u1 - 1.2, wr = 1.95;
    for (const s of [-0.9, 0.9]) box(g, F, hu + s - 0.06, hu + s + 0.06, deck, deck + 1.0, wr - 0.06, wr + 0.06, 'rawwood', WOOD_DARK, { skip: 'd' });
    beam(g, F.p(hu - 1.0, deck + 0.95, wr), F.p(hu + 1.0, deck + 0.95, wr), 0.09, 0.09, 'rawwood', WOOD_DARK);
    if (rq.next() < 0.6) {
      box(g, F, hu - 0.7, hu + 0.7, deck, deck + 0.5, wr - 0.62, wr - 0.12, 'planks', WOOD, { skip: 'd' });
      box(g, F, hu - 0.64, hu + 0.64, deck + 0.42, deck + 0.44, wr - 0.56, wr - 0.18, 'plain', lin(0x4f8fa0, 0.9), { skip: 'd' });
    }
  }
  }
  // sign board above the shopfront
  const signY0 = ty + 2.86, signY1 = ty + g0 - 0.18;
  const sh = signY1 - signY0, sw = Math.min(bw - 0.8, sh * 4);
  const signTile = SIGNS[ctx.signIx++ % SIGNS.length];
  if (by.roof !== 'false' || rng.next() < 0.4) {
    box(g, F, uc - sw / 2 - 0.08, uc + sw / 2 + 0.08, signY0 - 0.06, signY1 + 0.06, 0, 0.07, 'rawwood', WOOD_DARK, { skip: 'd' });
    decal(g, F, uc - sw / 2, uc + sw / 2, signY0, signY1, 0.075, signTile);
    // gooseneck lamps over the sign board
    { const rq = fork(rng); // quality-independent: the stream advances the same at every tier
    if (q >= 1 && rq.next() < 0.55) {
      for (const s2 of [-1, 1]) {
        const lu = uc + s2 * sw * 0.32;
        beam(g, F.p(lu, signY1 + 0.32, 0.02), F.p(lu, signY1 + 0.32, 0.5), 0.035, 0.035, 'metal', IRON);
        beam(g, F.p(lu, signY1 + 0.32, 0.5), F.p(lu, signY1 + 0.16, 0.5), 0.03, 0.03, 'metal', IRON);
        const lp = F.p(lu, 0, 0.5);
        crossCards(cut, lp[0], signY1 - 0.12, lp[2], 0.16, 0.28, 'lantern');
      }
    }
    }
  }
  // wall lantern beside the door, a poster or two
  { const rq = fork(rng); // quality-independent: the stream advances the same at every tier
  if (q >= 1) {
    const lu = du + (doorW / 2 + 0.45) * (rq.next() < 0.5 ? -1 : 1);
    beam(g, F.p(lu, deck + 2.2, 0.02), F.p(lu, deck + 2.2, 0.32), 0.04, 0.04, 'metal', IRON);
    const lp = F.p(lu, 0, 0.32);
    crossCards(cut, lp[0], deck + 1.85, lp[2], 0.2, 0.36, 'lantern');
    if (rq.next() < 0.7) {
      const pu = rq.next() < 0.5 ? u0 + 0.5 : u1 - 0.5;
      const pt = POSTERS[(rq.next() * POSTERS.length) | 0];
      decal(cut, F, pu - 0.24, pu + 0.24, deck + 1.0, deck + 1.66, 0.11, pt);
      const pu2 = pu + (pu < uc ? 0.55 : -0.55);
      if (Math.abs(pu2 - du) > doorW / 2 + 0.4) decal(cut, F, pu2 - 0.2, pu2 + 0.2, deck + 1.25, deck + 1.8, 0.03, POSTERS[(rq.next() * POSTERS.length) | 0]);
    }
    // ivy hanging off the eave or climbing a corner
    if (rq.next() < 0.4) {
      const left = rq.next() < 0.5;
      const va = left ? u0 + 0.05 : u1 - 1.45;
      const yTop = b.y1 - 0.15;
      decal(cut, F, va, va + 1.4, yTop - rq.range(2.0, 3.4), yTop, 0.12, 'vine', W1, 1, left ? 0 : 1, left ? 1 : 0);
    }
    if (rq.next() < 0.35) {
      const cu = rq.next() < 0.5 ? u0 + 0.5 : u1 - 0.5;
      decal(cut, F, cu - 0.6, cu + 0.6, deck - 0.05, deck + 1.6, 0.2, 'bush');
    }
  }
  }

  // ---- awning or porch roof over the boardwalk
  if (by.awning >= 0) awning(ctx, F, u0 + 0.14, u1 - 0.14, ty, AWN[by.awning], rng);
  else if (by.porch) porch(ctx, F, u0, u1, ty, by, rng);

  // hanging round sign on a bracket
  if (rng.next() < 0.7) {
    const su = rng.next() < 0.5 ? u0 + 0.35 : u1 - 0.35;
    const P0 = F.p(su, ty + 3.3, 0.05), P1 = F.p(su, ty + 3.3, 1.25);
    beam(g, P0, P1, 0.05, 0.05, 'metal', IRON);
    beam(g, F.p(su, ty + 2.75, 0.05), F.p(su, ty + 3.28, 0.8), 0.04, 0.04, 'metal', IRON);
    const S = F.sub(su, 0.75, -Math.PI / 2);
    const rt = RSIGN[(rng.next() * RSIGN.length) | 0];
    disc(g, S, 0, ty + 2.85, 0.03, 0.42, 16, rt);
    disc(g, new Frame(...frameArgs(S, 0, 0, Math.PI)), 0, ty + 2.85, 0.03, 0.42, 16, rt, W1, true);
    // edge band
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * Math.PI * 2, c = ((i + 1) / 16) * Math.PI * 2;
      g.quad(S.p(Math.cos(a) * 0.42, ty + 2.85 + Math.sin(a) * 0.42, -0.03), S.p(Math.cos(c) * 0.42, ty + 2.85 + Math.sin(c) * 0.42, -0.03),
        S.p(Math.cos(c) * 0.42, ty + 2.85 + Math.sin(c) * 0.42, 0.03), S.p(Math.cos(a) * 0.42, ty + 2.85 + Math.sin(a) * 0.42, 0.03),
        [0, 0], [0.1, 0], [0.1, 0.1], [0, 0.1], 'rawwood', WOOD_DARK);
    }
    beam(g, F.p(su, ty + 3.27, 0.75 - 0.0), F.p(su, ty + 3.27, 0.75), 0.02, 0.02, 'metal', IRON);
  }

  // ---- upper floors
  for (let f = 1; f < floors; f++) {
    const yfl = ty + g0 + (f - 1) * fh;
    const yc = yfl + fh * 0.5 + 0.05;
    const balconyHere = by.balcony && f === 1;
    const orielHere = by.oriel && !balconyHere && f === floors - 1;
    const n = Math.max(1, Math.floor((bw - 0.5) / 1.55));
    if (balconyHere) {
      balcony(ctx, F, u0 + 0.25, u1 - 0.25, yfl, by, rng);
    } else if (orielHere) {
      oriel(ctx, F, uc, Math.min(2.4, bw - 0.9), yfl + 0.35, yfl + fh - 0.15, by, rng);
      continue;
    }
    for (let k = 0; k < n; k++) {
      const u = u0 + (bw * (k + 0.5)) / n;
      if (balconyHere && Math.abs(u - uc) < bw / (2 * n) + 0.01 && n % 2 === 1) {
        // the balcony door
        hole(F, [u - 0.5, u + 0.5, yfl + 0.12, yfl + 2.22]);
        reveal(g, F, u - 0.5, u + 0.5, yfl + 0.12, yfl + 2.22, 0.14, 'trim', by.trim);
        decal(g, F, u - 0.5, u + 0.5, yfl + 0.12, yfl + 2.22, -0.14, by.accent[0] > by.accent[2] ? 'doorRed' : 'doorGreen');
        box(g, F, u - 0.62, u + 0.62, yfl + 2.22, yfl + 2.36, 0, 0.1, 'trim', by.trim, { skip: 'd' });
        continue;
      }
      if (by.tv && (k + f) % 2 === 0) tvWindow(ctx, F, u, yc, by);
      else windowUnit(ctx, F, u, yc, by, rng, true);
    }
  }
}

/** One double-hung window: glass decal in a reveal, casing, head, sill, shutters, flower box. */
function windowUnit(ctx: Ctx, F: Frame, u: number, yc: number, by: Bay, rng: Jit, front: boolean) {
  const { g, cut, q } = ctx;
  const w = 0.95, h = 1.42;
  const y0 = yc - h / 2, y1 = yc + h / 2;
  const tile = WIN[(rng.next() * WIN.length) | 0];
  hole(F, [u - w / 2, u + w / 2, y0, y1]);
  reveal(g, F, u - w / 2, u + w / 2, y0, y1, 0.2, 'trim', by.trim);
  decal(g, F, u - w / 2, u + w / 2, y0, y1, -0.2, tile, W1, 0.9);
  // the sash's meeting rail stands off the glass, so the opening parallaxes as you walk
  box(g, F, u - w / 2, u + w / 2, yc - 0.04, yc + 0.04, -0.2, -0.13, 'trim', by.trim);
  const tr = by.trim;
  box(g, F, u - w / 2 - 0.13, u - w / 2, y0 - 0.05, y1 + 0.05, -0.02, 0.07, 'trim', tr, { skip: 'd' });
  box(g, F, u + w / 2, u + w / 2 + 0.13, y0 - 0.05, y1 + 0.05, -0.02, 0.07, 'trim', tr, { skip: 'd' });
  // head with a little cornice cap
  box(g, F, u - w / 2 - 0.17, u + w / 2 + 0.17, y1 + 0.05, y1 + 0.22, -0.02, 0.09, 'trim', tr, { skip: 'd' });
  box(g, F, u - w / 2 - 0.23, u + w / 2 + 0.23, y1 + 0.22, y1 + 0.28, -0.02, 0.15, 'trim', tr, { skip: 'd', kTop: 1.05 });
  // sill + apron
  box(g, F, u - w / 2 - 0.2, u + w / 2 + 0.2, y0 - 0.1, y0 - 0.02, -0.02, 0.16, 'trim', tr);
  box(g, F, u - w / 2 - 0.08, u + w / 2 + 0.08, y0 - 0.26, y0 - 0.1, -0.02, 0.05, 'trim', tr, { skip: 'd' });
  if (false && q >= 1 && rng.next() < 0.55) grime(cut, F, u - w / 2 + 0.05, u + w / 2 - 0.05, y0 - 0.26, rng.range(0.3, 0.55), 0.055);
  if (front && by.shutters) {
    for (const s of [-1, 1]) {
      const a = u + s * (w / 2 + 0.16), c = a + s * 0.5;
      box(g, F, Math.min(a, c), Math.max(a, c), y0 - 0.02, y1 + 0.02, -0.01, 0.04, 'batten', by.accent, { skip: 'd' });
    }
  }
  { const rq = fork(rng); // quality-independent: the stream advances the same at every tier
  if (front && q >= 1 && rq.next() < 0.6) {
    // flower box
    box(g, F, u - w / 2 - 0.05, u + w / 2 + 0.05, y0 - 0.32, y0 - 0.06, 0.05, 0.32, 'rawwood', mul(by.accent, 1.1));
    const p = F.p(u, 0, 0.18);
    const fl = rq.next() < 0.6 ? 'flowers' : 'bush';
    decal(cut, F, u - w / 2 - 0.1, u + w / 2 + 0.1, y0 - 0.12, y0 + 0.36, 0.2, fl, W1, 1, 0.1, 0.9, fl === 'flowers' ? 0.35 : 0, 1);
    void p;
  }
  }
}

function tvWindow(ctx: Ctx, F: Frame, u: number, yc: number, by: Bay) {
  const { g } = ctx;
  // an old television set where the window used to be (the machines build with what they have)
  const w = 1.15, h = 1.15;
  box(g, F, u - w / 2, u + w / 2, yc - h / 2, yc + h / 2, -0.05, 0.32, 'rawwood', lin(0x9a6a40, 1.1), { front: 'tv' });
  box(g, F, u - w / 2 - 0.1, u + w / 2 + 0.1, yc - h / 2 - 0.12, yc - h / 2, -0.02, 0.4, 'trim', by.trim);
  // rabbit ears
  const top = F.p(u + 0.2, yc + h / 2, 0.12);
  beam(g, top, [top[0] - 0.25, top[1] + 0.55, top[2]], 0.02, 0.02, 'metal', IRON);
  beam(g, top, [top[0] + 0.3, top[1] + 0.5, top[2] + 0.05], 0.02, 0.02, 'metal', IRON);
}

function oriel(ctx: Ctx, F: Frame, uc: number, w: number, y0: number, y1: number, by: Bay, rng: Jit) {
  const { g } = ctx;
  const d = 0.62;
  const u0 = uc - w / 2, u1 = uc + w / 2;
  // body: front + sides of clapboard, windows on all three faces
  box(g, F, u0, u1, y0, y1, 0, d, by.wallTile, by.wall, { skip: 'bt' });
  const n = w > 1.9 ? 2 : 1;
  for (let k = 0; k < n; k++) {
    const u = u0 + (w * (k + 0.5)) / n;
    const ww = Math.min(0.85, w / n - 0.3);
    decal(g, F, u - ww / 2, u + ww / 2, y0 + 0.35, y1 - 0.3, d + 0.005, WIN[(rng.next() * WIN.length) | 0]);
    box(g, F, u - ww / 2 - 0.1, u + ww / 2 + 0.1, y0 + 0.25, y0 + 0.35, d, d + 0.1, 'trim', by.trim);
    box(g, F, u - ww / 2 - 0.08, u + ww / 2 + 0.08, y1 - 0.3, y1 - 0.2, d, d + 0.06, 'trim', by.trim);
  }
  for (const s of [-1, 1]) {
    const S = F.sub(s > 0 ? u1 : u0, d / 2, s > 0 ? -Math.PI / 2 : Math.PI / 2);
    decal(g, S, -0.18, 0.18, y0 + 0.4, y1 - 0.35, 0.005, 'win2', W1, 1, 0.2, 0.8);
  }
  // corner posts, base brackets and a shingled hip cap
  for (const u of [u0, u1]) box(g, F, u - 0.06, u + 0.06, y0 - 0.05, y1 + 0.02, d - 0.06, d + 0.06, 'trim', by.trim);
  box(g, F, u0 - 0.08, u1 + 0.08, y0 - 0.16, y0, -0.01, d + 0.1, 'trim', by.trim);
  for (const u of [u0 + 0.2, u1 - 0.2]) beam(g, F.p(u, y0 - 0.7, 0.02), F.p(u, y0 - 0.1, d - 0.05), 0.1, 0.1, 'trim', by.trim);
  const ry = y1 + 0.02, rh = 0.45;
  const a = F.p(u0 - 0.12, ry, d + 0.14), b = F.p(u1 + 0.12, ry, d + 0.14), c = F.p(u1 + 0.05, ry + rh, 0), e = F.p(u0 - 0.05, ry + rh, 0);
  g.quad(a, b, c, e, [u0 / 2, 0], [u1 / 2, 0], [u1 / 2, 0.4], [u0 / 2, 0.4], 'shingle', by.roofTile === 'shingle' ? by.roofCol : SHINGLE[0]);
  g.tri([F.p(u1 + 0.12, ry, d + 0.14), F.p(u1 + 0.12, ry, 0), F.p(u1 + 0.05, ry + rh, 0)], [[0, 0], [0.3, 0], [0.3, 0.3]], 'shingle', SHINGLE[0]);
  g.tri([F.p(u0 - 0.12, ry, 0), F.p(u0 - 0.12, ry, d + 0.14), F.p(u0 - 0.05, ry + rh, 0)], [[0, 0], [0.3, 0], [0, 0.3]], 'shingle', SHINGLE[0]);
}

function balcony(ctx: Ctx, F: Frame, u0: number, u1: number, yfl: number, by: Bay, rng: Jit) {
  const { g, cut, ty } = ctx;
  const d = 1.45;
  const yd = yfl;
  box(g, F, u0, u1, yd - 0.18, yd, 0, d, 'planks', WOOD, {});
  box(g, F, u0, u1, yd - 0.3, yd - 0.18, d - 0.12, d, 'trim', by.trim, { skip: 'b' });
  // railing: cut-out balusters front and sides + a solid cap rail
  const rh = 1.0;
  const M = 2.0;
  cut.quad(F.p(u0, yd, d - 0.05), F.p(u1, yd, d - 0.05), F.p(u1, yd + rh, d - 0.05), F.p(u0, yd + rh, d - 0.05), [u0 / M, 0], [u1 / M, 0], [u1 / M, 1], [u0 / M, 1], 'railing', by.trim);
  for (const u of [u0 + 0.05, u1 - 0.05]) cut.quad(F.p(u, yd, 0), F.p(u, yd, d - 0.05), F.p(u, yd + rh, d - 0.05), F.p(u, yd + rh, 0), [0, 0], [d / M, 0], [d / M, 1], [0, 1], 'railing', by.trim);
  box(g, F, u0 - 0.04, u1 + 0.04, yd + rh, yd + rh + 0.08, d - 0.1, d + 0.02, 'trim', by.trim);
  for (const u of [u0, u1]) box(g, F, u - 0.06, u + 0.06, yd, yd + rh + 0.08, d - 0.1, d + 0.02, 'trim', by.trim);
  // posts down to the boardwalk
  for (const u of [u0 + 0.12, u1 - 0.12]) box(g, F, u - 0.08, u + 0.08, ty + 0.15, yd - 0.18, d - 0.22, d - 0.06, 'trim', by.trim, { skip: 'd' });
  // a pot or two and a laundry line
  { const rq = fork(rng); // quality-independent: the stream advances the same at every tier
  if (ctx.q >= 1) {
    const p = F.p(u0 + 0.5, 0, d - 0.4);
    cyl(g, p[0], p[2], yd, yd + 0.32, 0.17, 0.22, 8, 'plain', lin(0xb4643c, 1.1), { top: 'rawwood', topCol: lin(0x5a4430) });
    crossCards(cut, p[0], yd + 0.25, p[2], 0.7, 0.7, rq.next() < 0.5 ? 'flowers' : 'bush', W1, 0.3);
    if (rq.next() < 0.6) hangStrip(cut, F.p(u0 + 0.1, yd + 2.0, 0.6), F.p(u1 - 0.1, yd + 2.0, 0.9), 0.18, 0.75, 'laundry', 3.0);
  }
  }
}

function porch(ctx: Ctx, F: Frame, u0: number, u1: number, ty: number, by: Bay, rng: Jit) {
  const { g } = ctx;
  const d = 2.05, yw = ty + 3.05, ye = ty + 2.7;
  // corrugated lean-to on posts
  const M = 2;
  const a = F.p(u0, yw, 0), b = F.p(u1, yw, 0), c = F.p(u1, ye, d), e = F.p(u0, ye, d);
  g.quad(e, c, b, a, [u0 / M, 0], [u1 / M, 0], [u1 / M, 1.05], [u0 / M, 1.05], 'corrugated', RUSTY[0]);
  g.quad(a, b, [c[0], c[1] - 0.04, c[2]], [e[0], e[1] - 0.04, e[2]], [u0, 0], [u1, 0], [u1, 2], [u0, 2], 'rawwood', mul(WOOD_DARK, 0.75));
  box(g, F, u0, u1, ye - 0.2, ye, d - 0.1, d + 0.02, 'rawwood', WOOD_DARK);
  if (!by.balcony) for (const u of [u0 + 0.15, u1 - 0.15]) box(g, F, u - 0.07, u + 0.07, ty + 0.15, ye - 0.2, d - 0.18, d - 0.04, 'rawwood', WOOD, { skip: 'd' });
  void rng;
}

function awning(ctx: Ctx, F: Frame, u0: number, u1: number, ty: number, tile: TileName, rng: Jit) {
  const { cut, g } = ctx;
  const yw = ty + 2.84, ye = ty + 2.26 + rng.range(-0.06, 0.06), d = 1.85, vh = 0.42;
  const M = 2.0;
  // sloped canvas (atlas v 1 -> 0.26 from wall to edge), valance hanging at the front edge
  const sag = 0.06;
  const N = 3;
  for (let i = 0; i < N; i++) {
    const t0 = i / N, t1 = (i + 1) / N;
    const y = (t: number) => yw + (ye - yw) * t - sag * Math.sin(Math.PI * t);
    const v = (t: number) => 1 - 0.74 * t;
    cut.quad(F.p(u0, y(t1), d * t1), F.p(u1, y(t1), d * t1), F.p(u1, y(t0), d * t0), F.p(u0, y(t0), d * t0),
      [u0 / M, v(t1)], [u1 / M, v(t1)], [u1 / M, v(t0)], [u0 / M, v(t0)], tile, W1);
  }
  cut.quad(F.p(u0, ye - vh, d), F.p(u1, ye - vh, d), F.p(u1, ye, d), F.p(u0, ye, d), [u0 / M, 0], [u1 / M, 0], [u1 / M, 0.26], [u0 / M, 0.26], tile, W1);
  // cheeks
  for (const u of [u0, u1]) cut.tri([F.p(u, yw, 0), F.p(u, ye, d), F.p(u, ye - 0.2, d)], [[0.05, 1], [0.05, 0.3], [0.05, 0.22]], tile, W1);
  // iron rod + brackets
  beam(g, F.p(u0, ye - 0.02, d - 0.02), F.p(u1, ye - 0.02, d - 0.02), 0.04, 0.04, 'metal', IRON);
  for (const u of [u0 + 0.1, u1 - 0.1]) beam(g, F.p(u, ty + 1.95, 0.02), F.p(u, ye - 0.05, d - 0.08), 0.045, 0.045, 'metal', IRON);
  // a small board hung under the awning rod
  { const rq = fork(rng); // quality-independent: the stream advances the same at every tier
  if (ctx.q >= 1 && rq.next() < 0.55) {
    const su = u0 + (u1 - u0) * rq.range(0.3, 0.7);
    const sw = 0.55, sh = 0.18;
    for (const s of [-0.4, 0.4]) beam(g, F.p(su + s, ye - 0.04, d - 0.06), F.p(su + s * 0.9, ye - 0.2, d - 0.06), 0.012, 0.012, 'metal', IRON);
    const S = F.sub(su, d - 0.06, 0);
    const st = SIGNS[(rq.next() * SIGNS.length) | 0];
    decal(g, S, -sw, sw, ye - 0.2 - 2 * sh, ye - 0.2, 0.02, st);
    decal(g, new Frame(...frameArgs(S, 0, 0, Math.PI)), -sw, sw, ye - 0.2 - 2 * sh, ye - 0.2, 0.02, st);
  }
  }
  { const rq = fork(rng); // quality-independent: the stream advances the same at every tier
  if (ctx.q >= 1 && rq.next() < 0.5) {
    for (const u of [u0 + 0.35, u1 - 0.35]) {
      const p = F.p(u, 0, d - 0.15);
      beam(g, [p[0], ye - 0.05, p[2]], [p[0], ye - 0.45, p[2]], 0.015, 0.015, 'metal', IRON);
      cyl(g, p[0], p[2], ye - 0.62, ye - 0.45, 0.14, 0.2, 8, 'rawwood', lin(0x8a6a48), { top: null });
      crossCards(cut, p[0], ye - 0.85, p[2], 0.6, 0.55, 'flowers', W1, 0.3, 0.1, 0.9);
    }
  }
  }
}

/** Exterior stair up the alley wall: stringers, treads, rails, a landing and a door. */
function stairs(ctx: Ctx, S: Frame, D: number, ty: number, ytop: number, by: Bay, rng: Jit) {
  const { g } = ctx;
  const w = 0.95;
  const run = Math.min(D - 1.6, (ytop - ty) * 1.3);
  const uStart = 0.5, uEnd = uStart + run;
  const n = Math.round((ytop - ty) / 0.2);
  const wo = 0.05, wi = wo + w;
  for (let i = 0; i < n; i++) {
    const ya = ty + ((i + 1) * (ytop - ty)) / n;
    const ua = uStart + (i * run) / n;
    box(g, S, ua, ua + run / n + 0.04, ya - 0.05, ya, wo + 0.05, wi - 0.05, 'planks', WOOD);
  }
  // stringers
  for (const ws of [wo, wi]) beam(g, S.p(uStart, ty, ws), S.p(uEnd, ytop, ws), 0.06, 0.28, 'rawwood', WOOD_DARK);
  // landing on posts
  box(g, S, uEnd, Math.min(D - 0.05, uEnd + 1.3), ytop - 0.15, ytop, wo, wi + 0.05, 'planks', WOOD);
  for (const u of [uEnd + 0.1, Math.min(D - 0.15, uEnd + 1.2)]) box(g, S, u - 0.07, u + 0.07, ty, ytop - 0.15, wi - 0.12, wi + 0.02, 'rawwood', WOOD_DARK, { skip: 'd' });
  // handrail + balusters on the open side
  beam(g, S.p(uStart, ty + 0.95, wi), S.p(uEnd, ytop + 0.95, wi), 0.06, 0.06, 'trim', by.trim);
  for (let i = 0; i <= 6; i++) {
    const t = i / 6;
    const p0 = S.p(uStart + run * t, ty + (ytop - ty) * t, wi), p1 = S.p(uStart + run * t, ty + (ytop - ty) * t + 0.95, wi);
    beam(g, p0, p1, 0.05, 0.05, 'trim', by.trim);
  }
  beam(g, S.p(uEnd, ytop + 0.95, wi), S.p(Math.min(D - 0.05, uEnd + 1.3), ytop + 0.95, wi), 0.06, 0.06, 'trim', by.trim);
  // upper door
  decal(g, S, uEnd + 0.15, uEnd + 1.05, ytop, ytop + 2.0, 0.015, rng.next() < 0.5 ? 'doorWood' : 'doorGreen');
}

// ------------------------------------------------------------------ roofs
function roof(ctx: Ctx, F: Frame, by: Bay, b: Box, y1: number, D: number, rng: Jit) {
  const { g } = ctx;
  const u0 = by.u0, u1 = by.u1, bw = u1 - u0, uc = (u0 + u1) / 2;
  const ovF = 0.4, ovB = 0.3;
  const ovL = by.outerL ? 0.3 : 0, ovR = by.outerR ? 0.3 : 0;
  const M = 2;
  const rt = by.roofTile, rc = by.roofCol;
  const trim = by.trim;
  const gableWall = (pts: number[][], wv: number) => {
    // a polygon in the facade plane (u, y) at depth wv, fan-triangulated, clapboard mapped
    const Mw = 2;
    for (let i = 1; i + 1 < pts.length; i++) {
      const a = pts[0], b = pts[i], c = pts[i + 1];
      const tri = wv >= 0 ? [a, b, c] : [a, c, b];
      g.tri(tri.map((p) => F.p(p[0], p[1], wv)), tri.map((p) => [(wv >= 0 ? p[0] : -p[0]) / Mw, p[1] / Mw]), by.wallTile, by.wall, tri.map(() => 0.92));
    }
  };
  const plane = (a: P3, b: P3, c: P3, d: P3, ua: number[], ub: number[], uc2: number[], ud: number[]) => {
    g.quad(a, b, c, d, ua, ub, uc2, ud, rt, rc);
    // underside (soffit) a touch lower, seen from the street
    g.quad(d.map((v, i) => i === 1 ? v - 0.06 : v), c.map((v, i) => i === 1 ? v - 0.06 : v), b.map((v, i) => i === 1 ? v - 0.06 : v), a.map((v, i) => i === 1 ? v - 0.06 : v),
      [0, 0], [1, 0], [1, 1], [0, 1], 'rawwood', mul(WOOD_DARK, 0.6));
  };

  if (by.roof === 'gable' || by.roof === 'gambrel') {
    const rh = Math.min(backThird(ctx, b) ? 4.6 : 3.6, (bw / 2) * by.pitch * (by.roof === 'gambrel' ? 1.25 : 1.1) * (backThird(ctx, b) ? 1.3 : 1));
    const wf = ovF, wb = -D - ovB;
    const ue0 = u0 - ovL - 0.25, ue1 = u1 + ovR + 0.25;
    const ye = y1 - 0.25 * (rh / (bw / 2));
    // profile points (u, y) from left eave over the ridge to the right eave
    const prof: number[][] = by.roof === 'gable'
      ? [[ue0, ye], [uc, y1 + rh], [ue1, ye]]
      : [[ue0, ye], [u0 + bw * 0.16, y1 + rh * 0.68], [uc, y1 + rh], [u1 - bw * 0.16, y1 + rh * 0.68], [ue1, ye]];
    let sAcc = 0;
    for (let i = 0; i + 1 < prof.length; i++) {
      const [ua, ya] = prof[i], [ub, yb] = prof[i + 1];
      const L = Math.hypot(ub - ua, yb - ya);
      // left half rises toward the ridge: winding so the normal faces up/out
      plane(F.p(ua, ya, wf), F.p(ub, yb, wf), F.p(ub, yb, wb), F.p(ua, ya, wb), [wf / M, sAcc / M], [wf / M, (sAcc + L) / M], [wb / M, (sAcc + L) / M], [wb / M, sAcc / M]);
      sAcc += L;
    }
    // gable walls front and back (clapboard), sitting on the box top
    const wallPts = by.roof === 'gable' ? [[u0, y1], [u1, y1], [uc, y1 + rh - 0.05]] : [[u0, y1], [u1, y1], [u1 - bw * 0.16, y1 + rh * 0.66], [uc, y1 + rh - 0.05], [u0 + bw * 0.16, y1 + rh * 0.66]];
    gableWall(wallPts, 0);
    gableWall(wallPts, -D);
    // barge boards along the front rake + a finial
    for (let i = 0; i + 1 < prof.length; i++) {
      const [ua, ya] = prof[i], [ub, yb] = prof[i + 1];
      beam(g, F.p(ua, ya - 0.08, wf + 0.03), F.p(ub, yb - 0.08, wf + 0.03), 0.08, 0.24, 'trim', trim);
    }
    beam(g, F.p(uc, y1 + rh, wf + 0.03), F.p(uc, y1 + rh + 0.5, wf + 0.03), 0.07, 0.07, 'trim', trim);
    beam(g, F.p(uc, y1 + rh + 0.02, wf + 0.06), F.p(uc, y1 + rh + 0.02, wb - 0.06), 0.3, 0.1, 'metal', RIDGE);
    // attic window in the gable
    if (rh > 1.6) {
      const ay = y1 + rh * 0.42;
      if (rng.next() < 0.5) {
        disc(g, F, uc, ay, 0.02, 0.38, 16, 'winRound');
        for (let i = 0; i < 16; i++) {
          const a = (i / 16) * Math.PI * 2, c = ((i + 1) / 16) * Math.PI * 2;
          beam(g, F.p(uc + Math.cos(a) * 0.44, ay + Math.sin(a) * 0.44, 0.04), F.p(uc + Math.cos(c) * 0.44, ay + Math.sin(c) * 0.44, 0.04), 0.13, 0.08, 'trim', trim);
        }
      } else {
        decal(g, F, uc - 0.38, uc + 0.38, ay - 0.5, ay + 0.55, 0.01, WIN[(rng.next() * WIN.length) | 0]);
        box(g, F, uc - 0.5, uc + 0.5, ay - 0.62, ay - 0.5, 0, 0.12, 'trim', trim);
        box(g, F, uc - 0.5, uc + 0.5, ay + 0.55, ay + 0.68, 0, 0.1, 'trim', trim);
        for (const s of [-1, 1]) box(g, F, uc + s * 0.38 - 0.06, uc + s * 0.38 + 0.06, ay - 0.5, ay + 0.55, 0, 0.06, 'trim', trim);
      }
    }
    chimney(ctx, F, uc + bw * rng.range(-0.25, 0.25), -D * rng.range(0.55, 0.85), y1 + rh * 0.55, y1 + rh + rng.range(0.6, 1.3), rng);
    if (backThird(ctx, b) && rng.next() < 0.6) cupola(ctx, F, uc, -D * 0.5, y1 + rh - 0.3, by, rng);
    return;
  }

  if (by.roof === 'side') {
    // ridge along the street, eaves front and back, a dormer facing the street
    const rh = Math.min(3.0, (D / 2) * by.pitch * 0.62);
    const wr = -D / 2;
    const ue0 = u0 - ovL, ue1 = u1 + ovR;
    const yeF = y1 - ovF * (rh / (D / 2)), yeB = y1 - ovB * (rh / (D / 2));
    const Lf = Math.hypot(D / 2 + ovF, rh), Lb = Math.hypot(D / 2 + ovB, rh);
    plane(F.p(ue0, yeF, ovF), F.p(ue1, yeF, ovF), F.p(ue1, y1 + rh, wr), F.p(ue0, y1 + rh, wr), [ue0 / M, 0], [ue1 / M, 0], [ue1 / M, Lf / M], [ue0 / M, Lf / M]);
    plane(F.p(ue1, yeB, -D - ovB), F.p(ue0, yeB, -D - ovB), F.p(ue0, y1 + rh, wr), F.p(ue1, y1 + rh, wr), [-ue1 / M, 0], [-ue0 / M, 0], [-ue0 / M, Lb / M], [-ue1 / M, Lb / M]);
    beam(g, F.p(ue0 - 0.06, y1 + rh + 0.02, wr), F.p(ue1 + 0.06, y1 + rh + 0.02, wr), 0.3, 0.1, 'metal', RIDGE);
    // gable ends (clapboard) on the outer ends
    const SR = F.sub(u1, 0, -Math.PI / 2), SL = F.sub(u0, -D, Math.PI / 2);
    const tri = (S: Frame) => g.tri([S.p(0, y1, 0), S.p(D, y1, 0), S.p(D / 2, y1 + rh - 0.04, 0)], [[0, y1 / 2], [D / 2, y1 / 2], [D / 4, (y1 + rh) / 2]], by.wallTile, mul(by.wall, 0.95), [0.92, 0.92, 0.92]);
    tri(SR); tri(SL);
    // eave fascia
    box(g, F, ue0, ue1, yeF - 0.2, yeF + 0.02, ovF - 0.06, ovF + 0.02, 'trim', trim);
    // dormer
    if (bw > 3.2) {
      const dw = 1.5, du0 = uc - dw / 2, du1 = uc + dw / 2;
      const wd = -0.4; // dormer face sits a little back from the facade
      const dTop = y1 + 1.75;
      const slopeY = (w: number) => y1 + rh * (w - 0) / (wr - 0) * 1;
      box(g, F, du0, du1, slopeY(wd) - 0.3, dTop, wd - 1.6, wd, by.wallTile, by.wall, { skip: 'btd' });
      decal(g, F, uc - 0.4, uc + 0.4, slopeY(wd) + 0.05, dTop - 0.15, wd + 0.01, WIN[(rng.next() * WIN.length) | 0]);
      box(g, F, uc - 0.52, uc + 0.52, slopeY(wd) - 0.05, slopeY(wd) + 0.05, wd, wd + 0.12, 'trim', trim);
      const ph = 0.7;
      g.quad(F.p(du0 - 0.15, dTop, wd + 0.2), F.p(uc, dTop + ph, wd + 0.2), F.p(uc, dTop + ph, wd - 1.8), F.p(du0 - 0.15, dTop, wd - 1.8), [0, 0], [0.5, 0], [0.5, 1], [0, 1], rt, rc);
      g.quad(F.p(uc, dTop + ph, wd + 0.2), F.p(du1 + 0.15, dTop, wd + 0.2), F.p(du1 + 0.15, dTop, wd - 1.8), F.p(uc, dTop + ph, wd - 1.8), [0, 0], [0.5, 0], [0.5, 1], [0, 1], rt, rc);
      g.tri([F.p(du0, dTop, wd), F.p(du1, dTop, wd), F.p(uc, dTop + ph - 0.05, wd)], [[0, 0], [0.75, 0], [0.37, 0.3]], by.wallTile, by.wall);
      beam(g, F.p(du0 - 0.15, dTop - 0.05, wd + 0.22), F.p(uc, dTop + ph - 0.05, wd + 0.22), 0.06, 0.16, 'trim', trim);
      beam(g, F.p(uc, dTop + ph - 0.05, wd + 0.22), F.p(du1 + 0.15, dTop - 0.05, wd + 0.22), 0.06, 0.16, 'trim', trim);
    }
    chimney(ctx, F, u0 + bw * rng.range(0.15, 0.3), wr + 0.3, y1 + rh * 0.6, y1 + rh + rng.range(0.8, 1.4), rng);
    if (backThird(ctx, b) && rng.next() < 0.7) cupola(ctx, F, uc + bw * 0.15, wr, y1 + rh - 0.3, by, rng);
    return;
  }

  if (by.roof === 'hip') {
    // four slopes to a short ridge, deep eaves on brackets, a chimney and (back third) a cupola
    const ue0 = u0 - ovL - 0.3, ue1 = u1 + ovR + 0.3, wf = ovF + 0.05, wb = -D - ovB;
    const W = ue1 - ue0, Dd = wf - wb;
    const inset = Math.min(W, Dd) / 2;
    const rh = Math.min(backThird(ctx, b) ? 3.8 : 3.0, inset * by.pitch * 0.9);
    const ye = y1 - 0.18, yr = ye + rh;
    // ridge runs along the longer side
    const alongU = W >= Dd;
    const ra: P3 = alongU ? F.p(ue0 + inset, yr, (wf + wb) / 2) : F.p((ue0 + ue1) / 2, yr, wf - inset);
    const rb: P3 = alongU ? F.p(ue1 - inset, yr, (wf + wb) / 2) : F.p((ue0 + ue1) / 2, yr, wb + inset);
    const eFL = F.p(ue0, ye, wf), eFR = F.p(ue1, ye, wf), eBR = F.p(ue1, ye, wb), eBL = F.p(ue0, ye, wb);
    const uvp = (p: P3, q2: P3, r2: P3, s2: P3) => {
      // planar map each slope by its own length along the eave and up the slope
      const L = Math.hypot(q2[0] - p[0], q2[2] - p[2]);
      const S = Math.hypot(rh, inset);
      return [[0, 0], [L / M, 0], [L / M, S / M], [0, S / M]];
    };
    const slope = (p: P3, q2: P3, r2: P3, s2: P3) => { const t2 = uvp(p, q2, r2, s2); plane(p, q2, r2, s2, t2[0], t2[1], t2[2], t2[3]); };
    // front, right, back, left (eave pair then the ridge end above them, CCW from outside)
    if (alongU) {
      slope(eFL, eFR, rb, ra);
      slope(eFR, eBR, rb, rb);
      slope(eBR, eBL, ra, rb);
      slope(eBL, eFL, ra, ra);
    } else {
      slope(eFL, eFR, ra, ra);
      slope(eFR, eBR, rb, ra);
      slope(eBR, eBL, rb, rb);
      slope(eBL, eFL, ra, rb);
    }
    beam(g, ra, rb, 0.26, 0.1, 'metal', RIDGE);
    for (const e of alongU ? [[eFL, ra], [eFR, rb], [eBR, rb], [eBL, ra]] : [[eFL, ra], [eFR, ra], [eBR, rb], [eBL, rb]]) beam(g, e[0], e[1], 0.18, 0.08, 'metal', RIDGE);
    // eave fascia + brackets along the front
    box(g, F, ue0, ue1, ye - 0.22, ye + 0.02, wf - 0.06, wf + 0.02, 'trim', trim);
    for (let u = u0 + 0.4; u < u1 - 0.2; u += 0.9) box(g, F, u - 0.05, u + 0.05, ye - 0.5, ye - 0.05, 0, wf - 0.08, 'trim', trim, { skip: 'd' });
    chimney(ctx, F, uc + bw * rng.range(-0.2, 0.2), -D * rng.range(0.4, 0.7), yr - 0.6, yr + rng.range(0.7, 1.3), rng);
    if (backThird(ctx, b) && rng.next() < 0.6) cupola(ctx, F, uc, -D * 0.5, yr - 0.3, by, rng);
    return;
  }

  // false front: a tall parapet with a cornice and the big sign, shed roof behind it
  const pf = backThird(ctx, b) ? rng.range(3.0, 4.6) : rng.range(1.5, 2.6);
  if (backThird(ctx, b) && rng.next() < 0.75) cupola(ctx, F, uc - bw * 0.15, -D * 0.45, y1 + 0.3, by, rng, 3.4);
  const shape = (rng.next() * 3) | 0;
  const top: number[][] = shape === 0
    ? [[u0, y1 + pf], [u1, y1 + pf]]
    : shape === 1
      ? [[u0, y1 + pf * 0.72], [u0 + bw * 0.22, y1 + pf * 0.72], [u0 + bw * 0.22, y1 + pf], [u1 - bw * 0.22, y1 + pf], [u1 - bw * 0.22, y1 + pf * 0.72], [u1, y1 + pf * 0.72]]
      : [[u0, y1 + pf * 0.7], [u0 + bw * 0.25, y1 + pf * 0.86], [uc, y1 + pf * 1.05], [u1 - bw * 0.25, y1 + pf * 0.86], [u1, y1 + pf * 0.7]];
  const poly = [[u0, y1], [u1, y1], ...top.slice().reverse()];
  // front face + back face of the parapet
  for (let i = 1; i + 1 < poly.length; i++) {
    const a = poly[0], b = poly[i], c = poly[i + 1];
    g.tri([a, b, c].map((p) => F.p(p[0], p[1], 0)), [a, b, c].map((p) => [p[0] / 2, p[1] / 2]), by.wallTile, by.wall, [0.95, 0.95, 0.95]);
    g.tri([a, c, b].map((p) => F.p(p[0], p[1], -0.2)), [a, c, b].map((p) => [-p[0] / 2, p[1] / 2]), 'plain', mul(by.wall, 0.8));
  }
  // cornice along the top outline + brackets
  for (let i = 0; i + 1 < top.length; i++) {
    const [ua, ya] = top[i], [ub, yb] = top[i + 1];
    if (Math.abs(ua - ub) < 1e-3) { box(g, F, ua - 0.08, ua + 0.08, Math.min(ya, yb), Math.max(ya, yb), -0.2, 0.1, 'trim', trim); continue; }
    beam(g, F.p(ua - (i === 0 ? 0.12 : 0), ya + 0.07, -0.04), F.p(ub + (i + 2 === top.length ? 0.12 : 0), yb + 0.07, -0.04), 0.36, 0.16, 'trim', trim);
  }
  box(g, F, u0 - 0.06, u1 + 0.06, y1 - 0.2, y1 + 0.05, -0.02, 0.18, 'trim', trim);
  for (let k = 0; k <= 4; k++) {
    const u = u0 + 0.2 + ((bw - 0.4) * k) / 4;
    box(g, F, u - 0.06, u + 0.06, y1 - 0.5, y1 - 0.2, 0, 0.15, 'trim', trim);
  }
  // parapet side edges
  for (const u of [u0, u1]) {
    const yTop = u === u0 ? top[0][1] : top[top.length - 1][1];
    box(g, F, u - (u === u0 ? 0 : 0.0), u, y1, yTop, -0.2, 0, 'trim', trim, { skip: 'fbtd' });
  }
  // the big sign on the parapet
  const sw = Math.min(bw - 0.9, 4.2), sh = sw / 4;
  const sy = y1 + Math.min(pf * 0.62, pf * 0.7 - 0.1) - sh / 2 + 0.05;
  box(g, F, uc - sw / 2 - 0.1, uc + sw / 2 + 0.1, sy - 0.1, sy + sh + 0.1, 0, 0.08, 'rawwood', WOOD_DARK, { skip: 'd' });
  decal(g, F, uc - sw / 2, uc + sw / 2, sy, sy + sh, 0.085, SIGNS[ctx.signIx++ % SIGNS.length]);
  // shed roof behind, falling to the back
  const ya = y1 + Math.min(pf * 0.6, 1.2), yb = y1 + 0.1;
  const L = Math.hypot(D + ovB, ya - yb);
  plane(F.p(u1 + ovR, ya, -0.2), F.p(u0 - ovL, ya, -0.2), F.p(u0 - ovL, yb, -D - ovB), F.p(u1 + ovR, yb, -D - ovB), [u1 / M, 0], [u0 / M, 0], [u0 / M, L / M], [u1 / M, L / M]);
  // close the sides under the shed roof
  for (const [u, s] of [[u0, -1], [u1, 1]] as const) {
    const S = F.sub(u, 0, s > 0 ? -Math.PI / 2 : Math.PI / 2);
    if (s > 0) g.tri([S.p(0.2, y1, 0), S.p(D, y1, 0), S.p(0.2, ya, 0)], [[0, y1 / 2], [D / 2, y1 / 2], [0, ya / 2]], by.wallTile, mul(by.wall, 0.92));
    else g.tri([S.p(-D, y1, 0), S.p(-0.2, y1, 0), S.p(-0.2, ya, 0)], [[0, y1 / 2], [D / 2, y1 / 2], [D / 2, ya / 2]], by.wallTile, mul(by.wall, 0.92));
  }
  // roof furniture: a washing-machine water tank on stilts, stove pipes, an aerial
  const r = rng.next();
  { const rq = fork(rng); // quality-independent: the stream advances the same at every tier
  if (r < 0.55 && ctx.q >= 1) roofTank(ctx, F, uc + bw * 0.15, -D * 0.6, y1 + 0.2, rq);
  else chimney(ctx, F, uc + bw * rq.range(-0.3, 0.3), -D * rq.range(0.5, 0.8), y1, y1 + pf + rq.range(0.3, 1.0), rq);
  }
  if (rng.next() < 0.5) {
    const p = F.p(u0 + 0.6, 0, -D * 0.4);
    cyl(g, p[0], p[2], y1, y1 + pf + 1.4, 0.1, 0.1, 6, 'metal', lin(0x8a8580));
    cyl(g, p[0], p[2], y1 + pf + 1.4, y1 + pf + 1.62, 0.22, 0.04, 6, 'metal', lin(0x6a6560), { top: null });
  }
}

/** The far third of the street, where the skyline needs to stack up toward the vanishing point. */
function backThird(ctx: Ctx, b: Box) {
  const t = ctx.world.town;
  return b.x > t.x0 + (t.x1 - t.x0) * 0.55;
}

/** A belfry / lookout cupola: louvred box, pyramid roof, finial (and a weathervane on some). */
function cupola(ctx: Ctx, F: Frame, u: number, w: number, y0: number, by: Bay, rng: Jit, rise = 0) {
  const { g } = ctx;
  const s = rng.range(0.75, 0.95);
  const yb = y0 + rise, h = rng.range(1.5, 2.1);
  if (rise > 0) box(g, F, u - s * 0.9, u + s * 0.9, y0, yb, w - s * 0.9, w + s * 0.9, by.wallTile, mul(by.wall, 0.95), { skip: 'd' });
  box(g, F, u - s, u + s, yb, yb + h, w - s, w + s, by.wallTile, by.wall, { skip: 'd' });
  const S4 = [F.sub(u, w + s + 0.005, 0), F.sub(u, w - s - 0.005, Math.PI), F.sub(u + s + 0.005, w, -Math.PI / 2), F.sub(u - s - 0.005, w, Math.PI / 2)];
  for (const S of S4) {
    decal(g, S, -s * 0.55, s * 0.55, yb + 0.35, yb + h - 0.3, 0, 'win2', W1, 0.9);
    box(g, S, -s * 0.7, s * 0.7, yb + 0.2, yb + 0.32, 0, 0.08, 'trim', by.trim);
  }
  for (const [a, c] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) box(g, F, u + a * s - 0.07, u + a * s + 0.07, yb, yb + h, w + c * s - 0.07, w + c * s + 0.07, 'trim', by.trim, { skip: 'd' });
  box(g, F, u - s - 0.12, u + s + 0.12, yb + h, yb + h + 0.14, w - s - 0.12, w + s + 0.12, 'trim', by.trim);
  // pyramid roof
  const ry = yb + h + 0.14, rp = rng.range(1.2, 1.8), o = s + 0.22;
  const apex = F.p(u, ry + rp, w);
  const c = [F.p(u - o, ry, w + o), F.p(u + o, ry, w + o), F.p(u + o, ry, w - o), F.p(u - o, ry, w - o)];
  const rc = by.roofTile === 'shingle' ? by.roofCol : SHINGLE[1];
  for (let i = 0; i < 4; i++) g.tri([c[i], c[(i + 1) % 4], apex], [[0, 0], [o, 0], [o / 2, rp / 2 + 0.5]], 'shingle', rc);
  beam(g, apex, [apex[0], apex[1] + 0.7, apex[2]], 0.05, 0.05, 'metal', IRON);
  if (rng.next() < 0.6) beam(g, [apex[0] - 0.35, apex[1] + 0.55, apex[2]], [apex[0] + 0.35, apex[1] + 0.55, apex[2]], 0.03, 0.12, 'metal', IRON);
}

function chimney(ctx: Ctx, F: Frame, u: number, w: number, y0: number, y1: number, rng: Jit) {
  const { g } = ctx;
  if (rng.next() < 0.6) {
    const s = rng.range(0.32, 0.42);
    box(g, F, u - s, u + s, y0, y1, w - s, w + s, 'brick', lin(0xffffff, 1.0), { skip: 'd' });
    box(g, F, u - s - 0.08, u + s + 0.08, y1, y1 + 0.14, w - s - 0.08, w + s + 0.08, 'stone', lin(0xd0c4b0), {});
    const p = F.p(u, 0, w);
    cyl(g, p[0], p[2], y1 + 0.14, y1 + 0.5, 0.12, 0.12, 6, 'metal', lin(0x9a6a50), { top: null });
  } else {
    const p = F.p(u, 0, w);
    cyl(g, p[0], p[2], y0, y1 + 0.4, 0.13, 0.13, 7, 'metal', lin(0x5a5550));
    cyl(g, p[0], p[2], y1 + 0.55, y1 + 0.85, 0.3, 0.05, 7, 'metal', lin(0x5a5550), { top: null });
    cyl(g, p[0], p[2], y1 + 0.4, y1 + 0.55, 0.13, 0.13, 7, 'metal', lin(0x5a5550), { top: null });
  }
  // an aerial on most
  if (rng.next() < 0.7) {
    const p = F.p(u + 0.6, 0, w + 0.4);
    const h = y1 + rng.range(1.2, 2.2);
    beam(g, [p[0], y0 - 0.3, p[2]], [p[0], h, p[2]], 0.04, 0.04, 'metal', IRON);
    for (let k = 0; k < 3; k++) {
      const yy = h - 0.15 - k * 0.32, hl = 0.55 - k * 0.1;
      beam(g, [p[0] - hl, yy, p[2]], [p[0] + hl, yy, p[2]], 0.025, 0.025, 'metal', IRON);
    }
  }
}

/** The machines' water: an old top-loader drum on a timber stand. */
function roofTank(ctx: Ctx, F: Frame, u: number, w: number, y0: number, rng: Jit) {
  const { g } = ctx;
  const p = F.p(u, 0, w);
  const r = 0.85, legH = 1.3;
  for (const [a, c] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) beam(g, [p[0] + a * 0.6, y0, p[2] + c * 0.6], [p[0] + a * 0.55, y0 + legH, p[2] + c * 0.55], 0.12, 0.12, 'rawwood', WOOD_DARK);
  box(g, WORLD, p[0] - 0.75, p[0] + 0.75, y0 + legH, y0 + legH + 0.12, p[2] - 0.75, p[2] + 0.75, 'planks', WOOD);
  const yb = y0 + legH + 0.12;
  cyl(g, p[0], p[2], yb, yb + 1.7, r, r, 16, 'enamel', lin(0xece6d6, 1.05), { top: 'enamel', topCol: lin(0x5f7fa6, 1.2) });
  cyl(g, p[0], p[2], yb + 1.25, yb + 1.45, r + 0.03, r + 0.03, 16, 'plain', lin(0x5f7fa6, 1.25), { top: null });
  // porthole toward the street
  const S = F.sub(u, w + r + 0.01, 0);
  disc(g, S, 0, yb + 0.75, 0, 0.5, 16, 'washer', W1);
  void rng;
}

// ------------------------------------------------------------------ street clutter
function streetClutter(ctx: Ctx, F: Frame, uc: number, span: number, rng: Jit, by: Bay) {
  // keep clear of the sim's cover crates on the boardwalk
  const cpos = F.p(uc, 0, 0.6);
  if (ctx.world.boxes.some((b) => b.kind === 'crate' && Math.abs(b.x - cpos[0]) < b.hx + span * 0.5 + 0.9 && Math.abs(b.z - cpos[2]) < b.hz + 1.2)) return;
  const { g, cut, ty, q } = ctx;
  const deck = ty + 0.15;
  const pick = rng.next();
  const u = uc + rng.range(-span * 0.3, span * 0.3);
  if (pick < 0.16) {
    // barrels
    const n = 1 + ((rng.next() * 2) | 0);
    for (let i = 0; i < n; i++) barrel(ctx, F.p(u + i * 0.66 - 0.3, 0, 0.42 + rng.range(-0.05, 0.1)), deck, rng);
  } else if (pick < 0.3) {
    // crate stack
    crate(ctx, F, u - 0.35, 0.4, deck, 0.62, rng);
    crate(ctx, F, u + 0.32, 0.38, deck, 0.55, rng);
    if (rng.next() < 0.6) crate(ctx, F, u - 0.25, 0.4, deck + 0.62, 0.5, rng);
  } else if (pick < 0.46) {
    // bench under the window + a potted plant
    box(g, F, u - 0.8, u + 0.8, deck + 0.42, deck + 0.48, 0.18, 0.55, 'planks', WOOD);
    for (const s of [-0.7, 0.7]) box(g, F, u + s - 0.04, u + s + 0.04, deck, deck + 0.42, 0.22, 0.5, 'rawwood', WOOD_DARK, { skip: 'd' });
    plant(ctx, F.p(u + 1.15, 0, 0.35), deck, rng);
  } else if (pick < 0.6) {
    // bicycle leaning on the wall
    const S = F.sub(u, 0.32, 0);
    decal(cut, S, -0.85, 0.85, deck, deck + 1.06, 0, 'bike');
  } else if (pick < 0.72) {
    plant(ctx, F.p(u - 0.4, 0, 0.35), deck, rng);
    plant(ctx, F.p(u + 0.35, 0, 0.32), deck, rng);
  } else if (pick < 0.82) {
    // chalkboard A-frame
    const S = F.sub(u, 1.15, rng.range(-0.4, 0.4));
    for (const s of [-1, 1]) {
      const a = S.p(-0.32, deck, s * 0.22), b = S.p(0.32, deck, s * 0.22), c = S.p(0.32, deck + 0.95, s * 0.02), d = S.p(-0.32, deck + 0.95, s * 0.02);
      if (s > 0) g.decal(a, b, c, d, 'chalk'); else g.decal(b, a, d, c, 'chalk');
    }
  } else if (pick < 0.9) {
    // wagon wheel against the wall + a milk churn
    const S = F.sub(u, 0.12, 0);
    const p = S.p(0, deck + 0.52, 0);
    const T = new Frame(p[0], p[2], S.ux, S.uz);
    // lean back a bit: build in a tilted frame by hand
    for (let i = 0; i < 1; i++) decal(cut, T, -0.52, 0.52, deck, deck + 1.04, 0.0, 'wheel');
    churn(ctx, F.p(u + 0.8, 0, 0.35), deck);
  } else if (pick < 0.95) {
    // a trestle table of goods
    box(g, F, u - 0.75, u + 0.75, deck + 0.72, deck + 0.78, 0.25, 0.95, 'planks', WOOD);
    for (const s of [-0.65, 0.65]) for (const w of [0.32, 0.88]) box(g, F, u + s - 0.03, u + s + 0.03, deck, deck + 0.72, w - 0.03, w + 0.03, 'rawwood', WOOD_DARK, { skip: 'd' });
    const goods: RGB[] = [lin(0xd44f2b, 1.1), lin(0xe3a21f, 1.1), lin(0x9bc349), lin(0xc9c4be, 1.1), lin(0x8fc7c3, 1.1)];
    for (let i = 0; i < 4; i++) {
      const gu = u - 0.55 + i * 0.37;
      box(g, F, gu - 0.15, gu + 0.15, deck + 0.78, deck + 0.9, 0.35, 0.65, 'rawwood', WOOD);
      box(g, F, gu - 0.12, gu + 0.12, deck + 0.9, deck + 0.98 + rng.range(0, 0.06), 0.38, 0.62, 'plain', goods[(rng.next() * goods.length) | 0]);
    }
  } else {
    // sacks on a pallet
    box(g, F, u - 0.6, u + 0.6, deck, deck + 0.12, 0.2, 0.95, 'planks', WOOD);
    for (let i = 0; i < 3; i++) {
      const su = u - 0.35 + i * 0.36;
      box(g, F, su - 0.17, su + 0.17, deck + 0.12, deck + 0.42 + rng.range(0, 0.08), 0.3, 0.85, 'plain', lin(0xcbb994, 1.05), {});
    }
  }
  // weeds at the foot of the wall
  { const rq = fork(rng); // quality-independent: the stream advances the same at every tier
  if (q >= 2 && rq.next() < 0.7) {
    const a = uc - span / 2, b = uc + span / 2;
    cut.quad(F.p(a, deck - 0.02, 0.06), F.p(b, deck - 0.02, 0.06), F.p(b, deck + 0.32, 0.03), F.p(a, deck + 0.32, 0.03), [a / 2, 0], [b / 2, 0], [b / 2, 1], [a / 2, 1], 'weeds', W1);
  }
  }
  void by;
}

/** The boardwalk lips 3-10 m ahead of the street entry (the view-0 frame): a dense, hand-placed
 *  run of bikes, stoops, posters, sandwich boards, crates and lanterns. Own stream, every tier. */
function nearDressing(ctx: Ctx) {
  const { g, cut, ty, world } = ctx;
  const t = world.town, deck = ty + 0.15;
  const rng = new Jit(hash32(world.seed, 0x4ea2, 11));
  const clear = (x: number, z: number, r: number) =>
    !world.boxes.some((b) => Math.abs(b.x - x) < b.hx + r && Math.abs(b.z - z) < b.hz + r) &&
    !world.cyls.some((c) => Math.hypot(c.x - x, c.z - z) < c.r + r);
  for (const side of [-1, 1]) {
    if (!ctx.decks.some((d) => d.side === side)) continue;
    const zl = t.z + side * (t.halfWidth + 0.45);       // just inside the deck lip
    const F = new Frame(0, zl, 1, 0);                   // u runs along x
    const kinds = side < 0 ? [0, 1, 2, 3, 4, 5] : [3, 2, 5, 0, 4, 1];
    let k = 0;
    for (let x = t.x0 + 6.5 + rng.range(0, 0.6); x < t.x0 + 15.5; x += rng.range(1.4, 2.0)) {
      const kind = kinds[k++ % kinds.length];
      const j = rng.range(-0.2, 0.2);
      if (!clear(x, zl, 0.7)) continue;
      if (kind === 0) {
        // a bicycle propped on its stand, side-on to the street
        decal(cut, new Frame(x, zl + side * 0.15, 1, 0), -0.85, 0.85, deck, deck + 1.06, 0, 'bike');
      } else if (kind === 1) {
        // a two-step stoop off the deck into the street
        for (let st = 0; st < 2; st++) {
          const zz = t.z + side * (t.halfWidth - 0.28 - st * 0.28);
          box(g, WORLD, x - 0.6, x + 0.6, ty - 0.1, deck - 0.06 - st * 0.07, Math.min(zz, zz + side * 0.3), Math.max(zz, zz + side * 0.3), 'planks', WOOD, {});
        }
      } else if (kind === 2) {
        // sandwich board
        const S = new Frame(x, zl, 1, 0);
        for (const sd of [-1, 1]) {
          const a = S.p(-0.32, deck, sd * 0.22), b = S.p(0.32, deck, sd * 0.22), c = S.p(0.32, deck + 0.95, sd * 0.02), d = S.p(-0.32, deck + 0.95, sd * 0.02);
          if (sd > 0) g.decal(a, b, c, d, 'chalk'); else g.decal(b, a, d, c, 'chalk');
        }
      } else if (kind === 3) {
        crate(ctx, F, x - 0.3, 0.1, deck, 0.6, rng);
        crate(ctx, F, x + 0.35, 0.05, deck, 0.5, rng);
        crate(ctx, F, x - 0.2, 0.1, deck + 0.6, 0.45, rng);
      } else if (kind === 4) {
        // lantern post on the lip
        box(g, WORLD, x - 0.05, x + 0.05, deck, deck + 2.3, zl - 0.05, zl + 0.05, 'rawwood', WOOD_DARK, { skip: 'd' });
        beam(g, [x, deck + 2.25, zl], [x + 0.35, deck + 2.25, zl], 0.035, 0.035, 'metal', IRON);
        crossCards(cut, x + 0.35, deck + 1.88, zl, 0.2, 0.36, 'lantern');
      } else {
        // a poster board on legs, facing the street, with a barrel beside it
        const S = new Frame(x, zl + side * 0.1, side < 0 ? 1 : -1, 0);
        for (const lu of [-0.4, 0.4]) box(g, S, lu - 0.03, lu + 0.03, deck, deck + 1.5, -0.03, 0.03, 'rawwood', WOOD_DARK, { skip: 'd' });
        box(g, S, -0.5, 0.5, deck + 0.55, deck + 1.55, 0.03, 0.06, 'planks', WOOD, {});
        decal(cut, S, -0.32, 0.32, deck + 0.65, deck + 1.45, -0.005, POSTERS[(rng.next() * POSTERS.length) | 0]);
        barrel(ctx, [x + 0.85, 0, zl + side * 0.15], deck, rng);
      }
      void j;
    }
  }
}

function barrel(ctx: Ctx, p: P3, y0: number, rng: Jit) {
  const { g } = ctx;
  const r = 0.29, h = 0.88;
  blob(ctx, p[0], p[2], r, y0);
  const col = mix(lin(0xffffff), lin(0xb07a50), rng.next() * 0.5);
  cyl(g, p[0], p[2], y0, y0 + h * 0.5, r * 0.88, r, 12, 'rawwood', col, { top: null, ao0: 0.6 });
  cyl(g, p[0], p[2], y0 + h * 0.5, y0 + h, r, r * 0.88, 12, 'rawwood', col, { top: 'rawwood', topCol: mul(col, 0.8) });
  for (const yy of [0.12, 0.42, 0.78]) {
    const rr = yy < 0.5 ? r * (0.88 + 0.12 * yy / 0.5) + 0.012 : r * (1 - 0.12 * (yy - 0.5) / 0.5) + 0.012;
    cyl(g, p[0], p[2], y0 + h * yy - 0.03, y0 + h * yy + 0.03, rr, rr, 12, 'metal', lin(0x6a625a), { top: null });
  }
}

function crate(ctx: Ctx, F: Frame, u: number, w: number, y0: number, s: number, rng: Jit) {
  const { g } = ctx;
  const col = mix(lin(0xffffff, 1.05), lin(0xc8b088), rng.next());
  const cp = F.p(u, 0, w);
  blob(ctx, cp[0], cp[2], s * 0.62, y0);
  box(g, F.sub(u, w, rng.range(-0.2, 0.2)), -s / 2, s / 2, y0, y0 + s, -s / 2, s / 2, 'planks', col, {});
}

function plant(ctx: Ctx, p: P3, y0: number, rng: Jit) {
  const { g, cut } = ctx;
  const big = rng.next() < 0.4;
  blob(ctx, p[0], p[2], big ? 0.4 : 0.24, y0);
  if (big) {
    box(g, WORLD, p[0] - 0.32, p[0] + 0.32, y0, y0 + 0.42, p[2] - 0.32, p[2] + 0.32, 'planks', lin(0xb08a60));
    crossCards(cut, p[0], y0 + 0.3, p[2], 1.0, 1.1, 'bush', W1, rng.range(0, 1));
  } else {
    cyl(g, p[0], p[2], y0, y0 + 0.36, 0.17, 0.23, 8, 'plain', lin(0xb4643c, 1.15), { top: 'rawwood', topCol: lin(0x4a3a28) });
    crossCards(cut, p[0], y0 + 0.26, p[2], 0.75, 0.75, rng.next() < 0.6 ? 'flowers' : 'bush', W1, rng.range(0, 1), 0.08, 0.92);
  }
}

function churn(ctx: Ctx, p: P3, y0: number) {
  const { g } = ctx;
  blob(ctx, p[0], p[2], 0.2, y0);
  cyl(g, p[0], p[2], y0, y0 + 0.55, 0.19, 0.19, 10, 'metal', lin(0xb8b2a8), { top: null });
  cyl(g, p[0], p[2], y0 + 0.55, y0 + 0.68, 0.19, 0.09, 10, 'metal', lin(0xb8b2a8), { top: null });
  cyl(g, p[0], p[2], y0 + 0.68, y0 + 0.76, 0.12, 0.12, 10, 'metal', lin(0x8a847a));
}

// ------------------------------------------------------------------ the street
/**
 * Cobbled main street between the curbs: small rounded field stones with dirt and moss in the
 * joints, a bleached and worn centre track, greener and darker toward the gutters. It rides a
 * few centimetres over the ground (and over the trail ribbon, which runs through the town).
 */
function street(ctx: Ctx) {
  const { g, ty, world } = ctx;
  seamGrass(ctx);
  const t = world.town;
  const xa = t.x0 - 5, xb = t.x1 + 3;
  const zw = t.halfWidth - 0.3;
  const NX = Math.ceil((xb - xa) / 0.8), NZ = 14;
  const M = 2.0;
  // the warm sun, gold sky fill and print grade push everything yellow: the tint leans blue so the
  // street lands on the ref's pale sand (~#c3a875) rather than ochre
  const SB: RGB = [0.9, 1.05, 1.4];
  const worn = mulv(lin(0xfffcf6, 1.12), SB), mid = mulv(lin(0xfffaf4, 1.0), SB), gutter = mulv(lin(0xb4b4a0, 0.8), SB);
  const P: P3[] = [], T: RGB[] = [];
  for (let i = 0; i <= NX; i++) {
    const x = xa + ((xb - xa) * i) / NX;
    for (let k = 0; k <= NZ; k++) {
      const z = t.z - zw + (2 * zw * k) / NZ;
      let y = world.terrain(x, z);
      if (y < -1e8) y = ty;
      // ends feather into the ground
      const endF = Math.min(1, (x - xa) / 3, (xb - x) / 2);
      y += 0.03 + 0.08 * endF;
      P.push([x, y, z]);
      const f = Math.abs((z - t.z) / zw);
      const n = nfbm(world.seed ^ 0x5717, x / 4, z / 4, 3);
      // the cart track: two bleached ruts either side of the crown
      const rut = Math.exp(-Math.pow((f - 0.35) / 0.18, 2));
      let c = mix(mid, worn, ss(0.5, 0.08, f) * 0.6);
      // the ruts are worn and stained dark by cart wheels and run-off, broken along their length
      const rb = nfbm(world.seed ^ 0x3a7, x / 3, z, 2);
      c = mix(c, mulv(c, [0.74, 0.68, 0.6]), rut * (0.35 + 0.5 * ss(0.35, 0.65, rb)));
      c = mix(c, gutter, ss(0.62, 1.0, f) * 0.85 + ss(0.62, 0.8, n) * 0.3);
      c = mul(c, 0.92 + 0.16 * n);
      // macro variation so the cobble never reads as one carpet: big sandy-dirt washes and
      // paler swept patches a few metres across
      const n2 = nfbm(world.seed ^ 0x2b1d, x / 9, z / 6, 3), n3 = nfbm(world.seed ^ 0x77e1, x / 5.5, z / 4.5, 2);
      c = mix(c, mulv(c, [0.97, 0.93, 0.86]), ss(0.5, 0.75, n2) * 0.7);
      c = mul(c, 0.86 + 0.26 * n3);
      // sandy drifts silting over the stones, and a few dark stains
      const nS = nfbm(world.seed ^ 0x51d, x / 3.5, z / 2.5, 3), nD = nfbm(world.seed ^ 0xd17, x / 2.2, z / 2.2, 2);
      c = mix(c, mulv(lin(0xfff1d6, 1.15), SB), ss(0.5, 0.7, nS) * 0.85);
      c = mix(c, mulv(c, [0.66, 0.6, 0.52]), ss(0.62, 0.78, nD) * 0.75);
      T.push(c);
    }
  }
  // a dark, damp gutter line hard against each kerb
  for (const side of [-1, 1]) {
    const zi = t.z + side * (zw - 0.4), zo = t.z + side * (zw + 0.05);
    const yAt = (x: number, z: number) => { const h = world.terrain(x, z); return (h < -1e8 ? ty : h) + 0.03 + 0.08 + 0.008; };
    for (let x = xa + 3; x < xb - 2; x += 0.8) {
      const x2 = Math.min(xb - 2, x + 0.8);
      const a: P3 = [x, yAt(x, zi), zi], b: P3 = [x2, yAt(x2, zi), zi], c: P3 = [x2, yAt(x2, zo), zo], d: P3 = [x, yAt(x, zo), zo];
      const gc = mulv(lin(0x8a6e52, 0.62), SB);
      // up-facing winding for either side
      if (side < 0) g.quad(a, b, c, d, [x / 3.6, zi / 3.6], [x2 / 3.6, zi / 3.6], [x2 / 3.6, zo / 3.6], [x / 3.6, zo / 3.6], 'cobble', gc, [1, 1, 0.7, 0.7]);
      else g.quad(b, a, d, c, [x2 / 3.6, zi / 3.6], [x / 3.6, zi / 3.6], [x / 3.6, zo / 3.6], [x2 / 3.6, zo / 3.6], 'cobble', gc, [1, 1, 0.7, 0.7]);
    }
  }
  const at = (i: number, k: number) => i * (NZ + 1) + k;
  for (let i = 0; i < NX; i++) for (let k = 0; k < NZ; k++) {
    const a = at(i, k), b = at(i + 1, k), c = at(i + 1, k + 1), d = at(i, k + 1);
    const pa = P[a], pb = P[b], pc = P[c], pd = P[d];
    const uv = (p: P3) => [p[0] / M, p[2] / M];
    const ao = (p: P3) => 1 - 0.25 * ss(0.8, 1.0, Math.abs((p[2] - t.z) / zw));
    // up-facing: (x, z) winding a(x0,z0) -> d(x0,z1) -> c(x1,z1) -> b(x1,z0)
    g.tri([pa, pd, pc], [uv(pa), uv(pd), uv(pc)], 'cobble', mix(mix(T[a], T[d], 0.5), T[c], 0.33), [ao(pa), ao(pd), ao(pc)]);
    g.tri([pa, pc, pb], [uv(pa), uv(pc), uv(pb)], 'cobble', mix(mix(T[a], T[c], 0.5), T[b], 0.33), [ao(pa), ao(pc), ao(pb)]);
  }
}

// ------------------------------------------------------------------ boardwalks
function boardwalk(ctx: Ctx, row: Box[], side: number) {
  const { g, cut, ty, q } = ctx;
  if (!row.length) return;
  const t = ctx.world.town;
  const xa = Math.min(...row.map((b) => b.x - b.hx)) - 1.2, xb = Math.max(...row.map((b) => b.x + b.hx)) + 1.2;
  const zs = t.z + side * t.halfWidth, zw = t.z + side * (t.halfWidth + 2.2);
  const z0 = Math.min(zs, zw), z1 = Math.max(zs, zw);
  const deck = ty + 0.15;
  const M = 2;
  // deck (boards run along the street)
  g.quad([xa, deck, z1], [xb, deck, z1], [xb, deck, z0], [xa, deck, z0], [xa / M, z1 / M], [xb / M, z1 / M], [xb / M, z0 / M], [xa / M, z0 / M], 'planks', DECK_TINT,
    side > 0 ? [0.86, 0.86, 1, 1] : [1, 1, 0.86, 0.86]);
  // street fascia + stone curb
  const F = side > 0 ? new Frame(xb, zs, -1, 0) : new Frame(xa, zs, 1, 0); // W points into the street
  const len = xb - xa;
  box(g, F, 0, len, ty - 0.35, deck, -0.05, 0.0, 'rawwood', WOOD_DARK, { skip: 'btd' });
  // granite curb kerb: separate blocks so the joints read
  for (let x = -0.1, i = 0; x < len + 0.1; i++) {
    const L = Math.min(len + 0.1 - x, 0.7 + ((hash32(i, side, 41) & 255) / 255) * 0.6);
    box(g, F, x + 0.015, x + L - 0.015, ty - 0.4, ty + 0.23, 0.0, 0.25, 'stone', mul(mulv(lin(0xf3e8cc, 1.0), [2.1, 2.3, 3.6]), 0.9 + ((hash32(i, side, 43) & 255) / 255) * 0.14), { skip: 'd', kTop: 1.08 });
    x += L;
  }
  // ends
  for (const x of [xa, xb]) {
    const S = new Frame(x, z0, 0, x === xa ? -1 : 1);
    box(g, WORLD, x - 0.03, x + 0.03, ty - 0.35, deck, z0, z1, 'rawwood', WOOD_DARK, { skip: 'td' });
    void S;
  }
  // posts every 2 m on the fascia
  for (let x = xa + 1; x < xb - 0.5; x += 2.0) box(g, WORLD, x - 0.06, x + 0.06, ty - 0.35, deck + 0.02, zs - side * 0.02 - 0.06, zs - side * 0.02 + 0.06, 'rawwood', WOOD_DARK, { skip: 'd' });
  // short green tufts in the curb joint, in clumps rather than a hedge
  if (q >= 1) {
    const zc = zs - side * 0.32;
    for (let x = xa + 0.5, i = 0; x < xb - 0.5; i++) {
      const h = hash32(i, side, 0x7f);
      const L = 0.5 + (h & 255) / 255 * 1.2;
      if (((h >>> 8) & 255) < 120) {
        const ht = 0.14 + ((h >>> 16) & 255) / 255 * 0.14;
        cut.quad([x, ty + 0.02, zc], [x + L, ty + 0.02, zc], [x + L, ty + 0.02 + ht, zc - side * 0.04], [x, ty + 0.02 + ht, zc - side * 0.04], [x / 1.4, 0], [(x + L) / 1.4, 0], [(x + L) / 1.4, 1], [x / 1.4, 1], 'tufts', [1.25, 1.28, 0.8]);
      }
      x += L + 0.3 + ((h >>> 24) & 255) / 255 * 2.0;
    }
    // things set out at the street edge of the boardwalk: pots, boards, barrels, a bench
    const poles = ctx.world.cyls.filter((c) => c.kind === 'pole' && Math.sign(c.z - t.z) === side);
    const ze = zs + side * 0.45;
    // the sim's cover crates stand on the boardwalk: make each one a little goods pile
    const crates = ctx.world.boxes.filter((b) => b.kind === 'crate' && Math.sign(b.z - t.z) === side && b.x > xa && b.x < xb);
    for (const c of crates) {
      const rng = new Jit(hash32(Math.round(c.x * 10), side, 0xc4a7));
      const sx = rng.next() < 0.5 ? -1 : 1;
      barrel(ctx, [c.x + sx * (c.hx + 0.42), 0, c.z + rng.range(-0.2, 0.2)], deck, rng);
      if (rng.next() < 0.7) crate(ctx, WORLD, c.x - sx * (c.hx + 0.38), c.z + rng.range(-0.2, 0.2), deck, 0.55, rng);
      else plant(ctx, [c.x - sx * (c.hx + 0.35), 0, c.z], deck, rng);
      blob(ctx, c.x, c.z, c.hx * 1.05, deck);
    }
    for (let x = xa + 1.5, i = 0; x < xb - 1.5; i++) {
      const rng = new Jit(hash32(i, side, 0xed9e));
      // dense where the player walks in, thinning towards the far end of the street
      const fx = Math.min(1, Math.max(0, (x - t.x0) / Math.max(1, t.x1 - t.x0)));
      x += rng.range(0.9, 1.6) + 0.6 * fx;
      if (poles.some((p) => Math.abs(p.x - x) < 1.0)) continue;
      if (crates.some((c) => Math.abs(c.x - x) < c.hx + 1.4)) continue;
      if (rng.next() < 0.06 + 0.15 * fx) continue;
      const r = rng.next();
      if (r < 0.3 && rng.next() < 0.5) plant(ctx, [x + 0.55, 0, ze + side * 0.05], deck, rng);
      const E = new Frame(x, ze, side > 0 ? 1 : -1, 0); // W points away from the street (to the facades)
      if (r < 0.3) plant(ctx, [x, 0, ze + side * 0.1], deck, rng);
      else if (r < 0.48) {
        const S = E.sub(0, 0.1, rng.range(-0.5, 0.5));
        for (const s2 of [-1, 1]) {
          const a = S.p(-0.3, deck, s2 * 0.2), b = S.p(0.3, deck, s2 * 0.2), c = S.p(0.3, deck + 0.9, s2 * 0.02), d = S.p(-0.3, deck + 0.9, s2 * 0.02);
          if (s2 > 0) g.decal(a, b, c, d, 'chalk'); else g.decal(b, a, d, c, 'chalk');
        }
      } else if (r < 0.68) barrel(ctx, [x, 0, ze + side * 0.15], deck, rng);
      else if (r < 0.8) {
        box(g, E, -0.8, 0.8, deck + 0.42, deck + 0.48, 0.0, 0.38, 'planks', WOOD);
        for (const s2 of [-0.7, 0.7]) box(g, E, s2 - 0.04, s2 + 0.04, deck, deck + 0.42, 0.04, 0.34, 'rawwood', WOOD_DARK, { skip: 'd' });
      } else if (r < 0.86) { crate(ctx, E, 0, 0.35, deck, 0.6, rng); if (rng.next() < 0.5) crate(ctx, E, 0.05, 0.35, deck + 0.6, 0.45, rng); }
      else if (r < 0.91) churn(ctx, [x, 0, ze + side * 0.1], deck);
      else if (r < 0.96) {
        // a wagon wheel propped on the curb rail / a bike
        const S = E.sub(0, 0.12, 0);
        if (rng.next() < 0.5) decal(cut, S, -0.5, 0.5, deck, deck + 1.0, 0, 'wheel');
        else decal(cut, S, -0.8, 0.8, deck, deck + 1.0, 0, 'bike');
      } else barrel(ctx, [x, 0, ze + side * 0.15], deck, rng);
    }
  }
}

function alleys(ctx: Ctx, buildings: Box[]) {
  const { g, cut, ty, q } = ctx;
  // stuff in the gaps between buildings: barrels, crates, a wheel, a laundry line overhead.
  // Built at every tier: the shade trees live here, and layout must not change with quality.
  void q;
  const t = ctx.world.town;
  for (const side of [-1, 1]) {
    const row = buildings.filter((b) => Math.sign(b.z - t.z) === side).sort((a, b) => a.x - b.x);
    for (let i = 0; i + 1 < row.length; i++) {
      const a = row[i], b = row[i + 1];
      const x0 = a.x + a.hx, x1 = b.x - b.hx, gw = x1 - x0;
      if (gw < 1.0) continue;
      const rng = new Jit(hash32(ctx.world.seed, i, side * 13));
      const zf = t.z + side * (t.halfWidth + 2.2);
      const xm = (x0 + x1) / 2;
      barrel(ctx, [xm + rng.range(-0.2, 0.2) * gw, 0, zf + side * 1.6], ty, rng);
      if (rng.next() < 0.6) barrel(ctx, [xm + rng.range(-0.3, 0.3) * gw, 0, zf + side * 2.3], ty, rng);
      if (rng.next() < 0.7) crate(ctx, WORLD, xm + rng.range(-0.25, 0.25) * gw, zf + side * 3.4, ty, 0.7, rng);
      // one big shade tree per side near the start, its canopy reaching out over the street
      if (i === (side < 0 ? 0 : 1) && gw > 1.0) {
        const tx = xm, tz = zf + side * 0.9, th = 8.5;
        cyl(g, tx, tz, ty - 0.2, ty + th * 0.62, 0.48, 0.28, 11, 'rawwood', lin(0x8a7a66), { top: null, dx: 0, dz: -side * 1.4 });
        // a limb out over the boardwalk
        beam(g, [tx, ty + th * 0.5, tz - side * 1.1], [tx + 0.6, ty + th * 0.68, tz - side * 3.2], 0.16, 0.16, 'rawwood', lin(0x8a7a66));
        // a crown of overlapping leaf clusters: a big core, lobes out over the street, a top knot
        const cy0 = ty + th * 0.72;
        ctx.canopies.push({ x: tx, y: cy0, z: tz - side * 1.6, r: 2.3, tone: rng.next() });
        for (let k = 0; k < 6; k++) {
          const a = (k / 6) * Math.PI * 2 + rng.range(-0.3, 0.3);
          ctx.canopies.push({ x: tx + Math.cos(a) * rng.range(1.5, 2.2), y: cy0 + rng.range(-0.6, 1.0), z: tz - side * (1.6 + Math.sin(a) * rng.range(1.5, 2.4)), r: rng.range(1.4, 1.9), tone: rng.next() });
        }
        ctx.canopies.push({ x: tx + rng.range(-0.5, 0.5), y: cy0 + 2.0, z: tz - side * 1.8, r: 1.6, tone: rng.next() });
      }
      // a tree filling the wider gaps (the green mass over the rooftops in the ref)
      if (gw > 2.2 && rng.next() < 0.7) {
        const tx = xm + rng.range(-0.2, 0.2) * gw, tz = zf + side * rng.range(3.0, 5.0);
        const th = rng.range(4.5, 7.0);
        cyl(g, tx, tz, ty - 0.2, ty + th * 0.7, 0.18, 0.11, 7, 'rawwood', lin(0x9a8a78), { top: null, dx: rng.range(-0.3, 0.3) });
        for (let k = 0; k < 4; k++) {
          const cy = ty + th * 0.62 + k * th * 0.12;
          ctx.canopies.push({ x: tx + rng.range(-0.8, 0.8), y: cy, z: tz + rng.range(-0.8, 0.8), r: rng.range(1.1, 1.5) * (k === 3 ? 0.8 : 1), tone: rng.next() });
        }
      }
      // weeds at the alley mouth
      cut.quad([x0, ty - 0.02, zf + side * 0.4], [x1, ty - 0.02, zf + side * 0.4], [x1, ty + 0.45, zf + side * 0.4], [x0, ty + 0.45, zf + side * 0.4], [x0 / 2, 0], [x1 / 2, 0], [x1 / 2, 1], [x0 / 2, 1], 'weeds', W1);
      if (rng.next() < 0.75) {
        const y = Math.min(a.y1, b.y1) - rng.range(1.2, 2.2);
        hangStrip(cut, [x0, y, zf + side * rng.range(1.0, 3.0)], [x1, y, zf + side * rng.range(1.0, 3.0)], 0.25, 0.7, 'laundry', 3.0);
        ctx.wires.push(x0, y, zf + side * 2, x1, y, zf + side * 2);
      }
      void g;
    }
  }
}

// ------------------------------------------------------------------ poles + wires
function wiresAndPoles(ctx: Ctx, poles: Cyl[]) {
  const { g, cut, ty, wires } = ctx;
  const t = ctx.world.town;
  const tops: { arms: P3[]; low: P3[]; x: number; z: number; side: number }[] = [];
  for (let i = 0; i < poles.length; i++) {
    const c = poles[i];
    const rng = new Jit(hash32(ctx.world.seed, i, 0x9013));
    const lean = 0.06;
    const dx = rng.range(-lean, lean), dz = rng.range(-lean, lean);
    cyl(g, c.x, c.z, c.y0, c.y1, c.r * 1.05, c.r * 0.78, 8, 'rawwood', lin(0xc8b8a0, 1.0), { dx, dz, ao0: 0.7 });
    const at = (y: number) => [c.x + dx * (y - c.y0) / (c.y1 - c.y0), y, c.z + dz * (y - c.y0) / (c.y1 - c.y0)];
    const side = Math.sign(c.z - t.z);
    // crossarms run across the street direction (along z) so the wires run along x
    const arms: P3[] = [], low: P3[] = [];
    const ya = c.y1 - 0.45, yb = c.y1 - 1.25;
    const pa = at(ya), pb = at(yb);
    beam(g, [pa[0], ya, pa[2] - 1.15], [pa[0], ya, pa[2] + 1.15], 0.1, 0.12, 'rawwood', WOOD_DARK);
    beam(g, [pb[0], yb, pb[2] - 0.8], [pb[0], yb, pb[2] + 0.8], 0.09, 0.1, 'rawwood', WOOD_DARK);
    // braces
    beam(g, at(ya - 0.6), [pa[0], ya - 0.04, pa[2] - 0.7], 0.04, 0.04, 'metal', IRON);
    beam(g, at(ya - 0.6), [pa[0], ya - 0.04, pa[2] + 0.7], 0.04, 0.04, 'metal', IRON);
    for (const o of [-1.0, -0.55, 0.55, 1.0]) {
      const p = [pa[0], ya + 0.06, pa[2] + o];
      cyl(g, p[0], p[2], ya + 0.06, ya + 0.2, 0.045, 0.035, 6, 'plain', lin(0x7fb8a8, 1.1));
      arms.push([p[0], ya + 0.19, p[2]]);
    }
    for (const o of [-0.6, 0.6]) {
      cyl(g, pb[0], pb[2] + o, yb + 0.05, yb + 0.18, 0.04, 0.03, 6, 'plain', lin(0xe8e0d0, 1.05));
      low.push([pb[0], yb + 0.17, pb[2] + o]);
    }
    // transformer can on every other pole, a street lamp on the rest
    if (i % 2 === 0) {
      const p = at(c.y1 - 2.6);
      cyl(g, p[0] + 0.36, p[2], c.y1 - 3.1, c.y1 - 2.3, 0.25, 0.25, 10, 'metal', lin(0x9a948a), { top: 'metal' });
      beam(g, [p[0] + 0.1, c.y1 - 2.45, p[2]], [p[0] + 0.15, c.y1 - 2.45, p[2]], 0.08, 0.08, 'metal', IRON);
      wires.push(p[0] + 0.36, c.y1 - 2.3, p[2], pb[0], yb + 0.17, pb[2] + 0.6);
    } else {
      const s = -side;
      const p = at(c.y1 - 2.8);
      beam(g, p, [p[0], p[1] + 0.35, p[2] + s * 1.4], 0.06, 0.06, 'metal', IRON);
      const L = [p[0], p[1] + 0.35, p[2] + s * 1.4];
      cyl(g, L[0], L[2], L[1] - 0.22, L[1], 0.32, 0.06, 10, 'metal', lin(0x4a5a52), { top: null });
      crossCards(cut, L[0], L[1] - 0.48, L[2], 0.16, 0.28, 'lantern');
    }
    // step pegs + a poster stapled at eye level
    for (let k = 0; k < 6; k++) {
      const y = ty + 2.4 + k * 0.45;
      const p = at(y);
      const a = k % 2 ? 0 : Math.PI;
      beam(g, p, [p[0] + Math.cos(a) * 0.32, y, p[2] + Math.sin(a) * 0.02], 0.03, 0.03, 'metal', IRON);
    }
    if (ctx.q >= 1 && i % 3 !== 1) {
      const zf = c.z - side * (c.r + 0.01);
      const S = new Frame(c.x, zf, side > 0 ? -1 : 1, 0);
      decal(cut, S, -0.2, 0.2, ty + 1.3, ty + 1.85, 0, POSTERS[i % POSTERS.length]);
    }
    tops.push({ arms, low, x: c.x, z: c.z, side });
  }
  const sag = (a: P3, b: P3, extra = 0) => {
    const L = Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]);
    catenary(wires, a, b, 0.18 + L * 0.03 + extra);
  };
  for (let i = 0; i < tops.length; i++) {
    const A = tops[i];
    // along the street on the same side
    if (i + 2 < tops.length) {
      const B = tops[i + 2];
      for (let k = 0; k < A.arms.length; k++) sag(A.arms[k], B.arms[k], (k % 2) * 0.12);
      for (let k = 0; k < A.low.length; k++) sag(A.low[k], B.low[k], 0.2 + k * 0.1);
    }
    // zig-zag across the street
    if (i + 1 < tops.length) {
      const B = tops[i + 1];
      sag(A.arms[A.side > 0 ? 0 : 3], B.arms[B.side > 0 ? 0 : 3], 0.35);
      sag(A.low[0], B.low[1], 0.5);
    }
    // service drops to the nearest facades on this side
    let n = 0;
    const near = ctx.attach.filter((q) => Math.sign(q[2] - t.z) === A.side).sort((p, q) => Math.abs(p[0] - A.x) - Math.abs(q[0] - A.x));
    for (const q of near) { if (n++ >= 3) break; sag(A.low[A.side > 0 ? 0 : 1], q, 0.3); sag(A.arms[1], [q[0] + 0.25, q[1] - 0.1, q[2]], 0.4); }
  }
  // the first and last poles run on out of town
  if (tops.length >= 2) {
    for (const A of [tops[0], tops[1]]) for (const p of A.arms) sag(p, [p[0] - 34, p[1] - 1.5, p[2] + A.side * 6], 1.2);
    for (const A of [tops[tops.length - 1], tops[tops.length - 2]]) for (const p of A.arms.slice(0, 2)) sag(p, [p[0] + 22, p[1] - 3, p[2] + A.side * 4], 1.0);
  }
  // high wires and bunting from facade to facade across the street
  const L = ctx.tops.filter((p) => p.side < 0), R = ctx.tops.filter((p) => p.side > 0);
  for (let i = 0; i < L.length; i++) {
    const a = L[i];
    let best = R[0], bd = 1e9;
    for (const r of R) { const d = Math.abs(r.x - a.x - 3); if (d < bd) { bd = d; best = r; } }
    if (!best) continue;
    sag([a.x, a.y, a.z], [best.x, best.y, best.z], 0.7);
    if (i % 2 === 0) sag([a.x + 0.8, a.y - 0.3, a.z], [best.x - 1.5, best.y - 0.4, best.z], 0.9);
    if (i % 4 === 2 && ctx.q >= 1) hangStrip(cut, [a.x, a.y + 0.1, a.z + 0.15], [best.x, best.y + 0.1, best.z - 0.15], 1.2, 0.3, 'bunting', 1.8);
  }
}

// ------------------------------------------------------------------ the washing-machine water tower
function tank(ctx: Ctx, c: Cyl, poles: Cyl[]) {
  const { g, ty, wires } = ctx;
  const R = c.r;
  const top = c.y1;
  const base = ty + 2.6;
  // stone plinth, enamel drum, blue band, chrome rim, rusty conical lid
  cyl(g, c.x, c.z, c.y0, base, R, R, 20, 'stone', lin(0xd8ccb0, 1.05), { top: null, ao0: 0.6 });
  cyl(g, c.x, c.z, base, top - 0.6, R, R, 24, 'enamel', lin(0xefe8d8, 1.05), { top: null, ao0: 0.8 });
  cyl(g, c.x, c.z, top - 2.4, top - 1.9, R + 0.04, R + 0.04, 24, 'plain', lin(0x5f7fa6, 1.25), { top: null });
  cyl(g, c.x, c.z, top - 0.6, top, R + 0.06, R + 0.06, 24, 'metal', lin(0xc9c4be, 1.1), { top: null });
  cyl(g, c.x, c.z, top, top + 1.6, R + 0.25, 0.3, 24, 'corrugated', RUSTY[0], { top: 'metal' });
  // porthole facing the street
  const dz = -Math.sign(c.z) || -1;
  const F = new Frame(c.x, c.z + dz * (R + 0.01), dz > 0 ? 1 : -1, 0);
  disc(g, F, 0, (base + top) / 2 + 0.6, 0, 2.0, 24, 'washer', W1);
  // control panel: three knobs
  for (const u of [-1.0, 0, 1.0]) {
    const p = F.p(u, 0, 0.06);
    void p;
    disc(g, F, u, top - 1.25, 0.08, 0.22, 10, 'enamel', lin(0xc9c4be, 1.1));
  }
  // ladder up the side
  const S = new Frame(c.x + R + 0.05, c.z, 0, -1);
  for (const u of [-0.3, 0.3]) beam(g, S.p(u, base - 2.4, 0.05), S.p(u, top + 0.3, 0.05), 0.06, 0.06, 'metal', IRON);
  for (let y = base - 2.2; y < top; y += 0.4) beam(g, S.p(-0.3, y, 0.05), S.p(0.3, y, 0.05), 0.04, 0.04, 'metal', IRON);
  // wire in from the last pole
  if (poles.length) {
    const p = poles[poles.length - 1];
    catenary(wires, [p.x, p.y1 - 0.3, p.z], [c.x - R * 0.5, top - 0.8, c.z + dz * R * 0.9], 1.4);
  }
}

// ------------------------------------------------------------------ the end of the street
/**
 * Frame the view down the street: a timber water tower in the open plaza corner, wires and
 * bunting converging on the gate, a banner board hung across the last block, lamp posts and
 * goods piles along the plaza edges. Nothing here stands where the sim walks (legs and posts
 * are thin, the boards hang well overhead).
 */
function vista(ctx: Ctx, buildings: Box[], poles: Cyl[]) {
  const { g, cut, ty, q, world } = ctx;
  const t = world.town;
  const gates = world.boxes.filter((b) => b.kind === 'gate');
  const gy = (x: number, z: number) => { const y = world.terrain(x, z); return y < -1e8 ? ty : y; };
  const rng = new Jit(hash32(world.seed, 0x715a, 3));
  const ends: number[] = [];
  for (const side of [-1, 1]) {
    const row = buildings.filter((b) => Math.sign(b.z - t.z) === side);
    ends.push(row.length ? Math.max(...row.map((b) => b.x + b.hx)) : t.x1);
  }
  const gx = gates.length ? gates[0].x : t.x1 + 6;

  // timber water tower in whichever plaza corner the row leaves open
  const side = ends[0] < ends[1] ? -1 : 1;
  const open = side < 0 ? ends[0] : ends[1];
  if (gx - open > 4) {
    const cx = Math.min(gx - 2.5, open + 3.5), cz = t.z + side * (t.halfWidth + 3.0);
    const y0 = gy(cx, cz), H = 9.5, R = 1.9;
    for (const [lx, lz] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) {
      const bx = cx + lx * 1.9, bz = cz + lz * 1.9;
      cyl(g, bx, bz, y0 - 0.3, y0 + H, 0.14, 0.11, 6, 'rawwood', lin(0xb8a48a), { dx: -lx * 0.55, dz: -lz * 0.55, ao0: 0.7 });
    }
    for (const yy of [y0 + 3.2, y0 + 6.4]) {
      const k = 1.9 - 0.55 * (yy - y0) / H;
      for (const [a, b] of [[[-1, -1], [1, -1]], [[1, -1], [1, 1]], [[1, 1], [-1, 1]], [[-1, 1], [-1, -1]]]) {
        beam(g, [cx + a[0] * k, yy, cz + a[1] * k], [cx + b[0] * k, yy, cz + b[1] * k], 0.08, 0.1, 'rawwood', WOOD_DARK);
      }
    }
    const tb = y0 + H;
    box(g, WORLD, cx - 1.6, cx + 1.6, tb - 0.15, tb + 0.05, cz - 1.6, cz + 1.6, 'planks', WOOD);
    cyl(g, cx, cz, tb, tb + 3.0, R, R, 18, 'planks', lin(0xd8b890, 1.05), { top: null, ao0: 0.75 });
    for (const yy of [0.4, 1.5, 2.6]) cyl(g, cx, cz, tb + yy - 0.05, tb + yy + 0.05, R + 0.03, R + 0.03, 18, 'metal', lin(0x5a4a40), { top: null });
    cyl(g, cx, cz, tb + 3.0, tb + 4.2, R + 0.3, 0.15, 18, 'corrugated', RUSTY[1], { top: 'metal' });
    beam(g, [cx, tb + 4.2, cz], [cx, tb + 5.0, cz], 0.05, 0.05, 'metal', IRON);
    // its spout swung out over the plaza
    beam(g, [cx - side * 0.0, tb + 0.6, cz - side * R], [cx, tb - 1.8, cz - side * (R + 1.4)], 0.18, 0.18, 'metal', lin(0x6a5a50));
  }

  // wires and bunting converging on the gate
  if (gates.length) {
    const gtops: P3[] = gates.map((b) => [b.x, b.y1 + 0.1, b.z]);
    const near = ctx.tops.filter((p) => p.x > Math.min(...ends) - 14).sort((a, b) => b.x - a.x);
    for (let i = 0; i < near.length && i < 4; i++) {
      const a = near[i];
      for (const gt of gtops) {
        if (Math.sign(gt[2] - t.z) === a.side || i < 2) catenary(ctx.wires, [a.x, a.y, a.z], gt, 0.5 + 0.03 * Math.hypot(gt[0] - a.x, gt[2] - a.z));
      }
      if (q >= 1 && i < 3) {
        const gt = gtops[(i + (a.side > 0 ? 0 : 1)) % gtops.length];
        hangStrip(cut, [a.x, a.y + 0.05, a.z], gt, 0.9, 0.3, 'bunting', 1.8);
      }
    }
    if (poles.length) {
      const p = poles[poles.length - 1], p2 = poles[poles.length - 2] ?? p;
      for (const pp of [p, p2]) for (const gt of gtops) catenary(ctx.wires, [pp.x, pp.y1 - 0.3, pp.z], gt, 0.8);
    }
  }

  // a banner board across the last block, hung from two wires
  {
    const bx = Math.min(...ends) - 10, by = ty + 7.0, hw = 2.3;
    const z0 = t.z - 0.2;
    const S = new Frame(bx, z0, 0, 1); // faces down the street toward the player (-x)
    box(g, S, -hw - 0.08, hw + 0.08, by - 0.08, by + 1.18, -0.06, 0.06, 'rawwood', WOOD_DARK);
    decal(g, S, -hw, hw, by, by + 1.1, 0.065, SIGNS[(world.seed + 3) % SIGNS.length]);
    const S2 = new Frame(bx, z0, 0, -1); // the back face, for the walk back up the street
    decal(g, S2, -hw, hw, by, by + 1.1, 0.065, SIGNS[(world.seed + 5) % SIGNS.length]);
    for (const s2 of [-1, 1]) {
      const top: P3 = [bx, by + 1.18, z0 + s2 * hw * 0.8];
      for (const side2 of [-1, 1]) {
        const fz = t.z + side2 * (t.halfWidth + 2.2);
        if (Math.sign(s2) === side2) catenary(ctx.wires, top, [bx + rng.range(-1.5, 1.5), ty + 8.2, fz], 0.25);
      }
    }
  }

  // lamp posts and goods along the plaza edges
  for (const s of [-1, 1]) {
    const e = ends[s < 0 ? 0 : 1];
    const z = t.z + s * (t.halfWidth + 0.6);
    for (let x = e + 1.2; x < gx - 1.5; x += 3.2) {
      const y0 = gy(x, z);
      cyl(g, x, z, y0, y0 + 3.6, 0.08, 0.06, 8, 'metal', lin(0x3e4a44), { top: null, ao0: 0.7 });
      cyl(g, x, z, y0, y0 + 0.5, 0.16, 0.12, 8, 'metal', lin(0x3e4a44), { top: null });
      crossCards(cut, x, y0 + 3.45, z, 0.24, 0.42, 'lantern');
      const r2 = new Jit(hash32(Math.round(x * 10), s, 0x91a));
      const gz = z + s * 1.2;
      const yg = gy(x + 1.4, gz);
      if (r2.next() < 0.5) { barrel(ctx, [x + 1.2, 0, gz], yg, r2); barrel(ctx, [x + 1.85, 0, gz + s * 0.3], yg, r2); }
      else { crate(ctx, WORLD, x + 1.4, gz, yg, 0.6, r2); plant(ctx, [x + 2.1, 0, gz], yg, r2); }
    }
  }
}

/** Tufts of grass in the cobble joints: thick at the gutters, thin across the worn track. */
function seamGrass(ctx: Ctx) {
  const { cut, ty, world, q } = ctx;
  if (q < 1) return;
  const t = world.town;
  const xa = t.x0 - 5, xb = t.x1 + 3, zw = t.halfWidth - 0.3;
  const rng = new Jit(hash32(world.seed, 0x5ea, 7));
  // a grass verge along each gutter, against the curb
  // soft painterly grass clumps along each curb: two staggered rows of strips with gaps
  for (const side of [-1, 1]) {
    for (const row of [0.97, 0.9]) {
      for (let x = xa + 2; x < xb - 1.5; ) {
        const L = rng.range(1.2, 3.2);
        if (rng.next() < (row > 0.95 ? 0.95 : 0.7)) {
          const z0 = t.z + side * (row * zw + rng.range(-0.05, 0.05)), z1 = z0 + side * rng.range(-0.08, 0.08);
          const yA = Math.max(world.terrain(x, z0), ty - 1) + 0.04, yB = Math.max(world.terrain(x + L, z1), ty - 1) + 0.04;
          const h = rng.range(0.32, 0.55) * (row > 0.95 ? 1 : 0.7);
          const u0 = rng.range(0, 4), u1 = u0 + L / 1.4;
          const tint = mul([1.0, 1.32, 0.66], rng.range(0.95, 1.15)); // a lush green verge
          const a: P3 = [x, yA, z0], b: P3 = [x + L, yB, z1], c: P3 = [x + L, yB + h, z1 - side * 0.06], d: P3 = [x, yA + h, z0 - side * 0.06];
          cut.quad(a, b, c, d, [u0, 0], [u1, 0], [u1, 1], [u0, 1], 'tufts', tint);
        }
        x += L * rng.range(0.6, 1.1);
      }
    }
  }

  // grass clumps scattered through the stones: crowding the gutters and the crown, clear of the ruts
  for (let i = 0, m = 0; i < m; i++) { // retired: grass now lives in the cobble joints and along the kerbs
    const x = rng.range(xa + 1, xb - 1), f = rng.range(-0.95, 0.95), af = Math.abs(f);
    if (Math.exp(-Math.pow((af - 0.35) / 0.15, 2)) > rng.next() * 1.3) continue;
    const z = t.z + f * zw, y = Math.max(world.terrain(x, z), ty - 1) + 0.04;
    // a low leafy clump (the canopy builder at ground scale): reads as a mat of weeds in the joints
    const r = rng.range(0.16, 0.32) * (af > 0.7 ? 1.3 : 1);
    ctx.canopies.push({ x, y: y - r * 0.2, z, r, tone: rng.next(), weed: true });
  }
  const n = 0; // grass in the joints now lives in the cobble tile itself
  for (let i = 0; i < n; i++) {
    const x = rng.range(xa + 1, xb - 1), f = rng.range(-1, 1);
    const af = Math.abs(f);
    // keep the cart track clear, crowd the gutters
    if (rng.next() > 0.12 + 0.88 * ss(0.3, 0.95, af)) continue;
    const z = t.z + f * zw;
    let y = world.terrain(x, z);
    if (y < -1e8) y = ty;
    const endF = Math.min(1, (x - xa) / 3, (xb - x) / 2);
    y += 0.03 + 0.08 * endF;
    const w = rng.range(0.16, 0.34), h = rng.range(0.07, 0.16);
    const cu = rng.range(0, 0.8);
    crossCards(cut, x, y - 0.01, z, w, h, 'weeds', mul(W1, rng.range(0.8, 1.0)), rng.range(0, 3), cu, cu + 0.12);
  }
}

// ------------------------------------------------------------------ backdrop past the gate
/**
 * Three layers of flat skyline cards beyond the island edge (unreachable: the void), so the end of
 * the street steps back into the haze: gables, false fronts, towers, a water tower and poles.
 * Unlit vertex colours, one draw.
 */
export function buildBackdrop(world: World): THREE.BufferGeometry {
  const t = world.town;
  const pos: number[] = [], col: number[] = [];
  const rng = new Jit(hash32(world.seed, 0xbac, 9));
  // every vertex takes its block colour graded by height: the low ground haze lifts the feet,
  // the tops stay a touch darker so each block keeps its silhouette against the sky
  const vcol = (y: number, c: RGB) => { const k = 1.14 - 0.24 * ss(t.y - 6, t.y + 16, y); col.push(c[0] * k, c[1] * k, c[2] * k); };
  const push = (c: RGB, ...v: number[]) => { for (let i = 0; i < v.length; i += 3) { pos.push(v[i], v[i + 1], v[i + 2]); vcol(v[i + 1], c); } };
  const quad = (x: number, z0: number, z1: number, y0: number, ya: number, yb: number, c: RGB) => {
    push(c, x, y0, z0, x, y0, z1, x, yb, z1, x, y0, z0, x, yb, z1, x, ya, z0);
  };
  const tri = (x: number, a: number[], b: number[], c2: number[], c: RGB) => {
    push(c, x, a[1], a[0], x, b[1], b[0], x, c2[1], c2[0]);
  };
  const layers = [
    { x: t.x1 + 34, k: 0.0, h: [9, 17] },
    { x: t.x1 + 62, k: 0.4, h: [12, 22] },
    { x: t.x1 + 105, k: 0.7, h: [16, 30] },
  ];
  const near = lin(0xb09a72, 1.0), far = lin(0xd8c496, 1.0);
  const q4 = (a: number[], b: number[], c: number[], d: number[], k: RGB) => push(k, ...a, ...b, ...c, ...a, ...c, ...d);
  // the street runs on past the gate: two receding rows of fronts stepping back into deeper haze,
  // so the vanishing point is town, not mountain
  const rowNear = lin(0x8a7656, 1.0), rowFar = lin(0xc8b080, 1.0); // a warm golden haze band
  for (const s of [-1, 1]) {
    for (let x = t.x1 + 7 + rng.range(0, 2); x < t.x1 + 150; ) {
      const d = (x - t.x1) / 150;
      const w = rng.range(4.5, 8.5) * (1 + d * 0.8);
      const h = rng.range(8, 15) * (1 + d * 1.3);
      const z = t.z + s * (t.halfWidth + 0.6 + d * 3 + rng.range(0, 0.8));
      const y0 = t.y - 6, y1 = t.y + h;
      const c = mul(mix(rowNear, rowFar, Math.min(1, d * 1.15 + rng.range(-0.05, 0.05))), rng.range(0.82, 1.12));
      // visual only: never stand a ghost block on walkable ground
      const onLand = [[x - 1, z], [x + w + 1, z], [x - 1, z + s * 7], [x + w + 1, z + s * 7], [x + w / 2, z - s]].some(([px, pz]) => world.terrain(px, pz) > -1e8);
      if (onLand) { x += 2; continue; }
      q4([x, y0, z], [x + w, y0, z], [x + w, y1, z], [x, y1, z], c);
      // the near flank, a shade darker, so each front reads as a block
      q4([x, y0, z], [x, y1, z], [x, y1, z + s * 6], [x, y0, z + s * 6], mul(c, 0.86));
      const r = rng.next();
      if (r < 0.45) {
        // a gable end to the street
        push(mul(c, 0.94), x - 0.3, y1, z, x + w + 0.3, y1, z, x + w / 2, y1 + w * 0.5, z);
      } else if (r < 0.8) {
        // a stepped false front
        q4([x + w * 0.15, y1, z], [x + w * 0.85, y1, z], [x + w * 0.85, y1 + 1.6, z], [x + w * 0.15, y1 + 1.6, z], c);
      }
      if (rng.next() < 0.14) {
        // a church spire or a mill tower breaking the skyline
        const tx = x + w * 0.3, tw = 2.2 + d * 2, th = h * 0.9;
        q4([tx, y1, z], [tx + tw, y1, z], [tx + tw, y1 + th, z], [tx, y1 + th, z], mul(c, 0.95));
        push(mul(c, 0.9), tx - 0.3, y1 + th, z, tx + tw + 0.3, y1 + th, z, tx + tw / 2, y1 + th + tw * 2.2, z);
      }
      if (rng.next() < 0.3) { const px = x + w + 0.3; q4([px, t.y - 1, z - s * 1.5], [px + 0.3, t.y - 1, z - s * 1.5], [px + 0.3, y1 + 4, z - s * 1.5], [px, y1 + 4, z - s * 1.5], mul(c, 0.8)); }
      x += w + rng.range(0.2, 1.8);
    }
  }
  for (const L of layers) {
    const base = t.y - 30;
    const half = 30 + L.k * 60;
    for (let z = t.z - half; z < t.z + half; ) {
      const w = rng.range(4, 9) * (1 + L.k * 0.6);
      const h = rng.range(L.h[0], L.h[1]);
      const y1 = t.y + h;
      const c = mul(mix(near, far, Math.min(1, L.k + rng.range(-0.08, 0.08))), rng.range(0.84, 1.1));
      const x = L.x + rng.range(-3, 3);
      quad(x, z, z + w, base, y1, y1, c);
      const r = rng.next();
      if (r < 0.4) tri(x, [z - 0.3, y1], [z + w + 0.3, y1], [z + w / 2, y1 + w * 0.45], mul(c, 0.92));
      else if (r < 0.6) quad(x, z + w * 0.2, z + w * 0.8, y1, y1 + w * 0.3, y1 + w * 0.3, c);
      else if (r < 0.72) {
        // a tower with a spire
        const tz = z + w * rng.range(0.2, 0.6), tw = 1.8 + L.k * 1.5;
        quad(x - 0.2, tz, tz + tw, y1, y1 + h * 0.5, y1 + h * 0.5, mul(c, 0.96));
        tri(x - 0.2, [tz - 0.2, y1 + h * 0.5], [tz + tw + 0.2, y1 + h * 0.5], [tz + tw / 2, y1 + h * 0.5 + tw * 1.4], mul(c, 0.9));
      } else if (r < 0.8) {
        // a water tower on legs
        const tz = z + w / 2, R = 2 + L.k * 1.5;
        for (const s of [-0.7, 0.7]) quad(x - 0.3, tz + s * R - 0.15, tz + s * R + 0.15, y1, y1 + 5, y1 + 5, mul(c, 0.85));
        quad(x - 0.3, tz - R, tz + R, y1 + 5, y1 + 8, y1 + 8, c);
        tri(x - 0.3, [tz - R - 0.3, y1 + 8], [tz + R + 0.3, y1 + 8], [tz, y1 + 9.6], mul(c, 0.9));
      }
      // a chimney or a pole now and then
      if (rng.next() < 0.35) { const cz = z + w * rng.range(0.15, 0.85); quad(x - 0.1, cz, cz + 0.6 + L.k, y1, y1 + 2.2, y1 + 2.2, mul(c, 0.9)); }
      if (L.k < 0.5 && rng.next() < 0.3) { const pz = z + w + 0.4; quad(x - 1, pz, pz + 0.35, t.y - 2, y1 + 4, y1 + 4, mul(c, 0.8)); }
      z += w + rng.range(0, 1.5) * (1 - L.k);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  g.computeBoundingSphere();
  return g;
}
